<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="map-container" @click="isActiveImage = true" ref="container">
  <template v-if="!loading && zoom !== null">
    <div class="map-body">
    <vl-map
      :data-projection="projectionName"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      :keyboard-event-target="document"
      @pointermove="onMapPointerMove"
      @mounted="updateKeyboardInteractions"
      ref="map"
    >

      <vl-view
        :center.sync="center"
        :zoom.sync="zoom"
        :rotation.sync="rotation"
        :max-zoom="maxZoom"
        :max-resolution="Math.pow(2, image.zoom)"
        :extent="extent"
        :projection="projectionName"
        @mounted="viewMounted()"
        ref="view"
      />

      <vl-layer-tile :extent="extent" @mounted="addOverviewMap" ref="baseLayer">
        <vl-source-ims
          :projection="projectionName"
          :url="baseLayerURL"
          :tile-load-function="tileLoadFunction"
          :size="imageSize"
          :extent="extent"
          :nb-resolutions="image.zoom"
          ref="baseSource"
          @mounted="setBaseSource()"
          :transition="0"
          :tile-size="[tileSize, tileSize]"
        />
      </vl-layer-tile>

<!--      <vl-layer-image>-->
<!--        <vl-source-raster-->
<!--          v-if="baseSource && colorManipulationOn"-->
<!--          :sources="[baseSource]"-->
<!--          :operation="operation"-->
<!--          :lib="lib"-->
<!--        />-->
<!--      </vl-layer-image>-->

      <!-- One vector layer per selected annotation layer. (Dense UC segmentation is no longer a
           vector layer — it's the precomputed raster overlay; see syncSegmentationLayer.) -->
      <annotation-layer
        v-for="layer in selectedLayers"
        :key="'layer-'+layer.id"
        :index="index"
        :layer="layer"
      />

      <select-interaction v-if="activeSelectInteraction" :index="index" />
      <draw-interaction v-if="activeDrawInteraction" :index="index" />
      <modify-interaction v-if="activeModifyInteraction" :index="index" />

    </vl-map>

    <!-- Hover preview. Reads the index plane the click uses, so what you see under the cursor
         is exactly what a click will select. No request per move. -->
    <div
      v-if="annotationHover"
      class="annotation-hover-chip"
      :style="{left: annotationHover.left + 'px', top: annotationHover.top + 'px'}"
    >
      <span class="swatch" :style="{background: annotationHover.color}"></span>
      {{ annotationHover.name }}
    </div>

    <!-- top-center loading indicator: tiles and/or annotations currently loading -->
    <loading-indicator-overlay :tilesLoading="tilesLoading" :annotationsLoading="annotationsLoading" />

    <div v-if="annotationsUnavailable" class="tile-error-notice" role="status">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ $t('annotations-unavailable') }}</span>
    </div>

    <!-- Tile failures must be visible. A blank patch of slide reads as "no tissue here", which
         is a clinically dangerous thing to infer from a network error. -->
    <div v-if="tileErrorCount > 0" class="tile-error-notice" role="status">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ $t('tile-load-failed') }} ({{ tileErrorCount }})</span>
    </div>

    <!-- Segmentation hover tooltip: the class under the cursor (select tool + overlay on; only for
         currently-shown classes). Follows the cursor; never intercepts pointer events. -->
    <div v-if="segHover" class="seg-hover-tip" :style="{left: segHover.x + 14 + 'px', top: segHover.y + 14 + 'px'}">
      <span class="seg-hover-swatch" :style="{backgroundColor: segHover.color}"></span>
      <span class="seg-hover-name">{{ segHover.name }}</span>
    </div>

    <!-- live width × height while drawing a rectangle (fixed top-left) -->
    <div v-if="liveDrawSize" class="live-draw-size">
      <span class="lds-dim"><span class="lds-k">W</span>{{ liveDrawSize.width }}</span>
      <span class="lds-x">×</span>
      <span class="lds-dim"><span class="lds-k">H</span>{{ liveDrawSize.height }}</span>
      <span class="lds-unit">{{ liveDrawSize.unit }}</span>
    </div>

    <!-- UI: unified CanvasToolbar — one floating bar (design): panels menu (☰) + pipeline (▶)
         + active-tool summary + expandable draw-tools grid. Replaces the old left icon rail
         and the separate right draw-tools card. -->
    <div class="canvas-toolbar" :class="{collapsed: !toolsExpanded, 'flyout-collapsed': flyoutCollapsed}">
      <div class="ct-head">
        <!-- UI-6: when the flyout is collapsed this ☰ button is the only thing left;
             clicking it re-expands. When expanded it toggles the panels menu as before. -->
        <button v-if="isPanelDisplayed('hide-tools')" class="ct-menu ct-menu-primary" :class="{open: menuOpen}"
                @click.stop="onMenuButton()" v-tooltip="flyoutCollapsed ? $t('show') : $t('menu')">
          <i class="fas fa-bars"></i>
        </button>

        <template v-if="isPanelDisplayed('hide-tools')">
          <div class="ct-divider"></div>
          <button class="ct-menu" :class="{open: activePanel === 'pipelines'}"
                  @click="openPanelFromMenu('pipelines')" v-tooltip="$t('pipelines')">
            <i class="fas fa-project-diagram"></i>
          </button>
        </template>

        <template v-if="configUI['project-tools-main']">
          <div class="ct-divider"></div>
          <div class="ct-tool-body" @click="toggleTools()">
            <span class="ct-active"><i :class="activeToolIcon"></i></span>
            <span class="ct-label">{{ activeToolLabel }}</span>
            <span class="ct-caret"><i class="fas" :class="toolsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i></span>
          </div>
        </template>

        <div class="ct-divider"></div>
        <button class="ct-menu ct-collapse" @click.stop="collapseFlyout()" v-tooltip="$t('hide')">
          <i class="fas fa-angle-double-left"></i>
        </button>
        <div class="ct-divider"></div>
        <button class="ct-menu ct-close" @click="$emit('close')" v-tooltip="$t('button-close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- expandable draw-tools grid (headless DrawTools) -->
      <div class="ct-grid" v-show="toolsExpanded && configUI['project-tools-main']">
        <draw-tools :index="index" headless @screenshot="takeScreenshot()" />
      </div>

      <!-- panels menu popover (☰) -->
      <div v-show="menuOpen" class="menu-popover" v-click-outside="() => menuOpen = false">
        <template v-for="(group, gi) in panelGroups">
          <div v-if="gi > 0" :key="`sep-${gi}`" class="menu-sep"></div>
          <div :key="`h-${gi}`" class="grp-h">{{ $t(group.label) }}</div>
          <div v-for="item in group.items" :key="item.id"
               class="menu-item" :class="{active: item.dock ? isDockOpen(item.id) : activePanel === item.id}"
               @click="item.dock ? toggleDock(item.id) : openPanelFromMenu(item.id)">
            <span class="ico"><i :class="item.icon"></i></span>
            <span class="menu-item-label">{{ $t(item.label) }}</span>
            <span class="chev"><i class="fas fa-chevron-right"></i></span>
          </div>
        </template>
      </div>

      <!-- flyout cards (panel content) — open beside the toolbar -->
      <template v-if="isPanelDisplayed('hide-tools')">
        <div v-if="isPanelDisplayed('info')" class="panel-options" v-show="activePanel === 'info'">
          <flyout-header :title="$t('information')" @close="togglePanel('info')" />
          <information-panel :index="index" @openMetadata="togglePanel('metadata')" />
        </div>
        <div v-if="isPanelDisplayed('info')" class="panel-metadata" v-show="activePanel === 'metadata'">
          <flyout-header :title="$t('image-metadata')" @close="togglePanel('metadata')" />
          <metadata-panel :index="index" />
        </div>

        <div v-if="isPanelDisplayed('digital-zoom')" class="panel-options" v-show="activePanel === 'digital-zoom'">
          <flyout-header :title="$t('digital-zoom')" @close="togglePanel('digital-zoom')" />
          <digital-zoom :index="index" @resetZoom="$refs.view.animate({zoom: image.zoom})" @fitZoom="fitZoom" />
        </div>

        <div v-if="isPanelDisplayed('link') && nbImages > 1" class="panel-options" v-show="activePanel === 'link'">
          <flyout-header :title="$t('link')" @close="togglePanel('link')" />
          <link-panel :index="index" />
        </div>

        <div v-if="isPanelDisplayed('color-manipulation')" class="panel-options" v-show="activePanel === 'colors'">
          <flyout-header :title="$t('colors')" @close="togglePanel('colors')" />
          <color-manipulation :index="index" />
        </div>

        <div v-if="isPanelDisplayed('image-layers')" class="panel-options" v-show="activePanel === 'layers'">
          <flyout-header :title="$t('annotation-layers')" @close="togglePanel('layers')" />
          <layers-panel :index="index" :layers-to-preload="layersToPreload" />
        </div>

        <!-- UC segmentation raster overlay (spec 2026-06-19): a dock panel, present only when this
             image has a precomputed pyramid + a finished UC run. Toggle / opacity / per-class filter.
             It is a raster overlay, not an annotation layer. -->
        <div v-if="segmentation && segmentation.exists" class="panel-options" v-show="activePanel === 'segmentation'">
          <flyout-header :title="$t('segmentation')" @close="togglePanel('segmentation')" />
          <div class="seg-panel">
            <label class="seg-toggle">
              <input type="checkbox" v-model="segmentationVisible" />
              <span>{{ $t('show') }}</span>
            </label>
            <div class="seg-opacity">
              <span class="seg-lbl">{{ $t('opacity') }}</span>
              <input type="range" min="20" max="255" v-model.number="segmentationOpacity" :disabled="!segmentationVisible" />
            </div>
            <!-- Classes grouped under their ontology parent (e.g. UC), mirroring the ontology tree. -->
            <div v-for="(grp, gi) in segmentationGroups" :key="gi" class="seg-group">
              <div v-if="grp.name" class="seg-group-head">
                <span class="seg-swatch" :style="{backgroundColor: grp.color || '#888888'}"></span>
                <span class="seg-group-name">{{ grp.name }}</span>
              </div>
              <ul class="seg-classes" :class="{nested: !!grp.name}">
                <li v-for="cls in grp.classes" :key="cls.index">
                  <label class="seg-class">
                    <input type="checkbox"
                           :checked="!segmentationHiddenTerms.includes(cls.term_id)"
                           :disabled="!segmentationVisible"
                           @change="toggleSegmentationTerm(cls.term_id)" />
                    <span class="seg-swatch" :style="{backgroundColor: cls.color || '#00aa50'}"></span>
                    <span class="seg-name">{{ cls.name }}</span>
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div v-if="isPanelDisplayed('ontology') && terms && terms.length > 0" class="panel-options" v-show="activePanel === 'ontology'">
          <flyout-header :title="$t('terms')" @close="togglePanel('ontology')" />
          <ontology-panel :index="index" />
        </div>

        <div v-if="isPanelDisplayed('property')" class="panel-options" v-show="activePanel === 'properties'">
          <flyout-header :title="$t('properties')" @close="togglePanel('properties')" />
          <properties-panel :index="index" />
        </div>

        <div v-if="isPanelDisplayed('follow')" class="panel-options" v-show="activePanel === 'follow'">
          <flyout-header :title="$t('broadcast')" @close="togglePanel('follow')" />
          <follow-panel :index="index" :view="$refs.view" />
        </div>

        <div v-if="isPanelDisplayed('review') && canEdit" class="panel-options" v-show="activePanel === 'review'">
          <flyout-header :title="$t('review')" @close="togglePanel('review')" />
          <review-panel :index="index" />
        </div>

        <div class="panel-options panel-options-wide" v-show="activePanel === 'pipelines'">
          <flyout-header :title="$t('pipelines')" :sub="$t('on-this-image')" @close="togglePanel('pipelines')" />
          <pipelines-panel :index="index" />
        </div>
      </template>
    </div>

    <image-controls :index="index" class="image-controls-wrapper" />

    <div class="broadcast" v-if="imageWrapper.tracking.broadcast">
      <i class="fas fa-circle"></i> {{$t('live')}}
    </div>

    <rotation-selector class="rotation-selector-wrapper" :index="index" />

    <!-- Vertical layers-opacity control, docked under the rotation compass. Bound to the
         SAME store value as the Annotation-Layers panel slider, so the two stay in sync
         from either side. Only shown while at least one annotation layer is selected. -->
    <div v-if="hasSelectedAnnotationLayers" class="layers-opacity-fly"
         v-tooltip="{content: $t('layers-opacity'), placement: 'right'}">
      <input class="lof-slider" type="range" min="0" max="1" step="0.05"
             v-model="layersOpacityCtl" :aria-label="$t('layers-opacity')">
    </div>

    <div class="custom-overview" ref="overview">
      <p class="image-name" :class="{hidden: overviewCollapsed}">
        <image-name :image="image" />
      </p>
      <div class="nav-readout" :class="{hidden: overviewCollapsed}">
        <div class="nav-scale">
          <span class="nav-scale-bar" :style="{width: navScaleBarPx + 'px'}"></span>
          <span class="nav-scale-label">{{navScale}}</span>
        </div>
        <span v-if="navMagnification" class="nav-mag" :class="{'nav-mag-over': magnificationOver}">{{$t('magnification')}}: {{navMagnification}}X</span>
        <span v-if="projectedMousePosition" class="nav-coords">
          x {{Math.round(projectedMousePosition[0])}} · y {{Math.round(projectedMousePosition[1])}}
        </span>
      </div>
      <!-- UI-7 B9 (refined): slide-label toggle + browser full-screen toggle on one aligned
           row; full-screen is icon-only with a hover tooltip so it lines up with the label. -->
      <div class="sl-controls" :class="{hidden: overviewCollapsed}">
        <button v-if="image.macroURL && hasSlideLabel" class="sl-nav-toggle" :class="{open: showSlideLabel}"
                @click="showSlideLabel = !showSlideLabel">
          <i class="fas fa-qrcode"></i>
          <span>{{ $t('slide-label') }}</span>
        </button>
        <button class="sl-nav-toggle sl-icon-only" @click="toggleFullscreen()"
                v-tooltip="isFullscreen ? $t('exit-fullscreen') : $t('fullscreen')">
          <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
        </button>
      </div>
      <div v-if="image.macroURL && hasSlideLabel" v-show="showSlideLabel"
           class="sl-nav-card" :class="{hidden: overviewCollapsed}">
        <image-thumbnail
          :image="image"
          :label="true"
          :size="200"
          :key="`${image.id}-viewer-label`"
          :extra-parameters="{authorization: 'Bearer ' + shortTermToken}"
          @error="hasSlideLabel = false"
        />
      </div>
    </div>
    </div><!-- /.map-body -->
    <!-- Dock layout (2026-09-03): the annotations LIST docks as the first flex column of
         .map-container (display:contents promotion + order:-1, so DOM position is free),
         pushing the map right. Kept AFTER .map-body on purpose: the details card inside is
         a positioned sibling of the map and must PAINT ABOVE it (positioned siblings paint
         in DOM order) - placing it first put the annotation information behind the WSI. -->
    <annotations-container :index="index" @centerView="centerViewOnAnnot" />
  </template>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {updateToken} from '@/utils/token-utils';
import _ from 'lodash';

import ImageName from '@/components/image/ImageName';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import AnnotationLayer from './AnnotationLayer';
import RotationSelector from './RotationSelector';
import DrawTools from './DrawTools';
import ImageControls from './ImageControls';
import AnnotationsContainer from './AnnotationsContainer';

import InformationPanel from './panels/InformationPanel';
import MetadataPanel from './panels/MetadataPanel.vue';
import FlyoutHeader from './FlyoutHeader.vue';
import PipelinesPanel from './panels/PipelinesPanel.vue';
import DigitalZoom from './panels/DigitalZoom';
import ColorManipulation from './panels/ColorManipulation';
import LinkPanel from './panels/LinkPanel';
import LayersPanel from './panels/LayersPanel';
import OntologyPanel from './panels/OntologyPanel';
import PropertiesPanel from './panels/PropertiesPanel';
import FollowPanel from './panels/FollowPanel';
import ReviewPanel from './panels/ReviewPanel';

import SelectInteraction from './interactions/SelectInteraction';
import DrawInteraction from './interactions/DrawInteraction';
import ModifyInteraction from './interactions/ModifyInteraction';
import LoadingIndicatorOverlay from './LoadingIndicatorOverlay';

import {addProj, createProj, getProj} from 'vuelayers/lib/ol-ext';

import View from 'ol/View';
import OverviewMap from 'ol/control/OverviewMap';
import TileLayer from 'ol/layer/Tile';

// Above this many annotations in view we will NOT fall back to loading vectors when the
// renderer is unavailable. Matches the server's own VECTOR_MAX, which is the count below
// which the vector path is genuinely the better option.
const ANNOTATION_FALLBACK_MAX = 2000;
import TileImageSource from 'ol/source/TileImage';
import {DragPan, KeyboardPan, KeyboardZoom} from 'ol/interaction';
import {noModifierKeys, targetNotEditable} from 'ol/events/condition';
import WKT from 'ol/format/WKT';

import {Ims, ImageConsultation, Annotation, AnnotationType, UserPosition, SliceInstance} from '@/api';

// import {constLib, operation} from '@/utils/color-manipulation.js';

import constants from '@/utils/constants.js';
import {updateTermProperties, updateTrackProperties, updateAnnotationLinkProperties} from '@/utils/annotation-utils';
import OlImageLayer from 'ol/layer/Image';
import OlStaticImage from 'ol/source/ImageStatic';
import {isVRBrowser} from '@/utils/vr';
import pipelineService from '@/api/pipeline-service';
import SegmentationLabelProbe from '@/utils/segmentation-label-probe';

// When a UC pipeline run finishes, ims-service registers the precomputed segmentation pyramid in a
// background worker tick AFTER the run reaches FINISHED — so a refresh triggered the instant the run
// goes terminal can race ahead of the pyramid. Poll a few times before giving up.
const SEGMENTATION_RELOAD_MAX_ATTEMPTS = 15;
const SEGMENTATION_RELOAD_INTERVAL_MS = 2000;

export default {
  name: 'ims-image',
  props: {
    index: String
  },
  components: {
    FlyoutHeader,
    PipelinesPanel,
    ImageName,
    ImageThumbnail,

    AnnotationLayer,
    LoadingIndicatorOverlay,

    RotationSelector,
    DrawTools,
    ImageControls,
    AnnotationsContainer,

    InformationPanel,
    MetadataPanel,
    DigitalZoom,
    ColorManipulation,
    LinkPanel,
    LayersPanel,
    OntologyPanel,
    PropertiesPanel,
    FollowPanel,
    ReviewPanel,

    SelectInteraction,
    DrawInteraction,
    ModifyInteraction
  },
  data() {
    return {
      annotationHover: null,
      minZoom: 0,

      projectedMousePosition: [0, 0],

      baseSource: null,

      // UC segmentation raster overlay (spec 2026-06-19): the precomputed slide-aligned label-tile
      // pyramid served by ims-service. `segmentation` holds the metadata ({classes, ...}) when
      // the image has one; the overlay is a TileImage layer (this._segLayer) on the slide grid.
      segmentation: null,
      segmentationVisible: true,
      segmentationOpacity: 160,
      segmentationHiddenTerms: [], // term_ids toggled off (empty => all classes shown)
      // Hover probe tooltip: {name, color, x, y} (viewport px) when the cursor is over a *visible*
      // segmentation class with the select tool active; null otherwise.
      segHover: null,
      routedAnnotation: null,
      selectedAnnotation: null,

      // loading indicator (top-center): counts of in-flight tiles / annotation fetches,
      // with a short debounce on hide so the badge doesn't flicker between batches.
      tileLoadingCount: 0,
      annotationLoadingCount: 0,
      tilesLoading: false,
      tileErrorCount: 0,
      annotationsUnavailable: false,
      tileErrorTimer: null,
      annotationsLoading: false,
      tilesHideTimer: null,
      annotsHideTimer: null,

      // live {width, height, unit} while drawing a rectangle (null = not drawing)
      liveDrawSize: null,

      timeoutSavePosition: null,

      loading: true,

      // UI-5: slide-label card on the navigator (closed by default; hidden if no label).
      showSlideLabel: false,
      hasSlideLabel: true,

      overview: null,

      // unified CanvasToolbar: ☰ panels menu open + tool-grid expanded
      menuOpen: false,
      toolsExpanded: false,
      flyoutCollapsed: false, // UI-6: collapse the whole flyout down to just the ☰ icon

      // UI-7 B9: browser full-screen state
      isFullscreen: false,

      format: new WKT(),
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),

    // The raster is an annotation layer, so the existing annotation-opacity slider must drive
    // it. Applied client-side with setOpacity - putting opacity in the request would turn one
    // drag of the slider into hundreds of re-renders.
    // A stable string of the hidden term ids, so the watcher fires on a real change.
    annotationHiddenTermsKey() {
      return ((((this.imageWrapper || {}).style || {}).terms) || [])
        .filter(t => t.visible === false).map(t => t.id).sort().join(',');
    },

    // The raster is ONE picture for every layer, so unlike the vector plane it cannot be
    // hidden by removing a layer's OpenLayers layer - it has to be re-rendered without that
    // layer's annotations. The render endpoint already filters on `users`, and an annotation
    // layer IS a user (layer id 55 == sec_user 55 == the annotations' user_id, verified).
    // Returns null when nothing is visible, which means "draw no raster at all".
    annotationVisibleUserIds() {
      const sel = this.selectedLayers || [];
      if (!sel.length) {
        return null;
      }
      const ids = sel.filter(l => l.visible !== false).map(l => l.id);
      return ids.length ? ids : null;
    },

    annotationVisibleUserKey() {
      return (this.annotationVisibleUserIds || []).slice().sort().join(',');
    },

    hasSelectedAnnotationLayers() {
      return (((this.imageWrapper || {}).layers || {}).selectedLayers || []).length > 0;
    },
    /* One source of truth: the same imageWrapper.style.layersOpacity the LayersPanel slider
       binds to - moving either control moves both. */
    layersOpacityCtl: {
      get() {
        return this.imageWrapper.style.layersOpacity;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setLayersOpacity', Number(value));
      }
    },

    annotationRasterOpacity() {
      const st = this.imageWrapper && this.imageWrapper.style;
      const v = st && st.layersOpacity;
      return typeof v === 'number' ? v : 1;
    },

    document() {
      return document;
    },
    project: get('currentProject/project'),
    routedAction() {
      return this.$route.query.action;
    },
    configUI: get('currentProject/configUI'),
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    nbImages() {
      return Object.keys(this.viewerWrapper.images).length;
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    canEdit() {
      return this.$store.getters['currentProject/canEditImage'](this.image);
    },
    projectionName() {
      return `CYTO-${this.image.id}`;
    },
    terms() {
      return this.$store.getters['currentProject/terms'];
    },
    selectedLayers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },
    isActiveImage: {
      get() {
        return this.viewerWrapper.activeImage === this.index;
      },
      set(value) {
        if (value) {
          if (this.viewerWrapper) {
            this.$store.commit(this.viewerModule + 'setActiveImage', this.index);
          }
        } else {
          throw new Error('Cannot unset active map');
        }
      }
    },
    activePanel() {
      return this.imageWrapper.activePanel;
    },
    activeTool() {
      return this.imageWrapper.draw.activeTool;
    },
    // unified toolbar: active-tool summary shown in the head
    activeToolLabel() {
      return this.activeTool ? this.$t(this.activeTool) : this.$t('tools');
    },
    activeToolIcon() {
      const map = {
        select: 'fas fa-mouse-pointer',
        point: 'fas fa-map-pin',
        line: 'fas fa-minus',
        'freehand-line': 'fas fa-pen',
        rectangle: 'far fa-square',
        circle: 'far fa-circle',
        polygon: 'fas fa-draw-polygon',
        'freehand-polygon': 'fas fa-pen-nib',
        'magic-wand': 'fas fa-magic'
      };
      return map[this.activeTool] || 'fas fa-mouse-pointer';
    },
    // unified toolbar: panels grouped for the ☰ menu (same display gates as before)
    panelGroups() {
      let groups = [
        // Dock layout (2026-09-03): Images and the Annotation list moved from floating
        // bottom tabs into this menu - they open as LEFT-docked columns that push the
        // slide right instead of hovering over the tissue. `dock: true` items toggle a
        // dock instead of opening a flyout card.
        {label: 'panel-group-browse', items: [
          {id: 'images-dock', icon: 'far fa-images', label: 'images', dock: true},
          this.isPanelDisplayed('annotations-list')
            && {id: 'annotations-dock', icon: 'fas fa-list-ul', label: 'annotations-list', dock: true},
        ]},
        {label: 'panel-group-view', items: [
          this.isPanelDisplayed('info') && {id: 'info', icon: 'fas fa-info', label: 'information'},
          this.isPanelDisplayed('digital-zoom') && {id: 'digital-zoom', icon: 'fas fa-search', label: 'digital-zoom'},
          this.isPanelDisplayed('color-manipulation') && {id: 'colors', icon: 'fas fa-adjust', label: 'colors'},
          (this.isPanelDisplayed('link') && this.nbImages > 1) && {id: 'link', icon: 'fas fa-link', label: 'link'},
        ]},
        {label: 'panel-group-annotate', items: [
          this.isPanelDisplayed('image-layers') && {id: 'layers', icon: 'fas fa-copy', label: 'annotation-layers'},
          (this.segmentation && this.segmentation.exists) && {id: 'segmentation', icon: 'fas fa-layer-group', label: 'segmentation'},
          (this.isPanelDisplayed('ontology') && this.terms && this.terms.length > 0) && {id: 'ontology', icon: 'fas fa-hashtag', label: 'terms'},
          this.isPanelDisplayed('property') && {id: 'properties', icon: 'fas fa-tag', label: 'properties'},
        ]},
        {label: 'panel-group-collaborate', items: [
          this.isPanelDisplayed('follow') && {id: 'follow', icon: 'fas fa-street-view', label: 'broadcast'},
          (this.isPanelDisplayed('review') && this.canEdit) && {id: 'review', icon: 'fas fa-check-circle', label: 'review'},
        ]},
      ];
      return groups
        .map(g => ({label: g.label, items: g.items.filter(Boolean)}))
        .filter(g => g.items.length);
    },
    // Segmentation classes grouped under their ontology parent term (e.g. UC), so the panel mirrors
    // the ontology hierarchy instead of a flat list. Classes without a resolved term are dropped.
    segmentationGroups() {
      if (!this.segmentation || !this.segmentation.classes) {
        return [];
      }
      let groups = {};
      let order = [];
      this.segmentation.classes.forEach(cls => {
        if (cls.term_id == null) {
          return;
        }
        let key = cls.parent_name || '';
        if (!groups[key]) {
          groups[key] = {name: cls.parent_name, color: cls.parent_color, classes: []};
          order.push(key);
        }
        groups[key].classes.push(cls);
      });
      return order.map(k => groups[k]);
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    maxZoom() {
      return this.$store.getters[this.imageModule + 'maxZoom'];
    },
    center: {
      get() {
        return this.imageWrapper.view.center;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setCenter', {index: this.index, center: value});
      }
    },
    zoom: {
      get() {
        return this.imageWrapper.view.zoom;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setZoom', {index: this.index, zoom: Number(value)});
      }
    },
    rotation: {
      get() {
        return this.imageWrapper.view.rotation;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setRotation', {index: this.index, rotation: Number(value)});
      }
    },

    viewState() {
      return {center: this.center, zoom: this.zoom, rotation: this.rotation};
    },

    extent() {
      return [0, 0, this.image.width, this.image.height];
    },
    imageSize() {
      return [this.image.width, this.image.height];
    },
    tileSize() {
      return this.image.tileSize;
    },
    baseLayerProcessingParams() {
      return this.$store.getters[this.imageModule + 'tileRequestParams'];
    },
    baseLayerSliceParams() {
      return {
        zSlices: this.slices[0].zStack,
        timepoints: this.slices[0].time
      };
    },
    baseLayerURLQuery() {
      let query = new URLSearchParams({...this.baseLayerSliceParams, ...this.baseLayerProcessingParams}).toString();
      if (query.length > 0) {
        return `?${query}`;
      }
      return query;
    },
    baseLayerURL() {
      let slice = this.slices[0];
      return Ims.instance.host + Ims.instance.basePath + `sliceinstance/${slice.id}/normalized-tile/zoom/{z}/tx/{x}/ty/{y}.jpg${this.baseLayerURLQuery}`;
    },
    tileLoadFunction() {
      return async (tile, src) => {
        // Guard 58: the "Tiles loading" spinner is accounted HERE, not from OL tile events.
        // OpenLayers disposes tiles that leave the view mid-flight (any pan/zoom while tiles
        // are loading - the initial zoom animation alone does it) and fires NO
        // tileloadend/tileloaderror for them, so an event-driven counter can only leak upward
        // - observed stuck at 16 with the spinner spinning forever on an idle, fully-rendered
        // slide. This loader controls every terminal path, so it guarantees exactly one
        // decrement per invocation, disposed tiles included.
        this.onTileLoadStart();
        let ended = false;
        const endOnce = () => {
          if (!ended) {
            ended = true;
            this.onTileLoadEnd();
          }
        };
        const xhr = new XMLHttpRequest();
        const activeTenant = (constants.ACTIVE_TENANT || '').toString().trim().toLowerCase();
        const token = await this.authToken();
        xhr.responseType = 'blob';
        xhr.timeout = 30000; // a hung connection must become a terminal event, never a stuck tile
        xhr.open('GET', src);
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        if (activeTenant) {
          xhr.setRequestHeader('X-TENANT', activeTenant);
        }
        // P4 signed tiles. With this header the router verifies the HMAC itself and proxies
        // straight to PIMS - Core leaves the tile hot path. In a HEADER, deliberately not in
        // the URL: tiles are cached by full URL (browser + OpenLayers), so a rotating token
        // in the URL would re-download every visible tile on each refresh. No token (old
        // Core, feature off, fetch still in flight) means the router falls back to the Core
        // proxy path - behaviour identical, just slower.
        if (this._tileSig && this._tileSigSlice === this.slices[0].id) {
          xhr.setRequestHeader('X-Tile-Signature', this._tileSig);
        }
        // Every failure path must END the tile. OpenLayers' tile queue has a fixed number of
        // slots, so tiles that never resolve stall the whole map - the base slide included.
        // Deliberately NOT tile.setState(ERROR): OL never re-enqueues an ERROR tile
        // (renderer/Layer.js:224) and its TileCache pins the visible ones, so one blip would
        // leave a permanent hole. Instead retry once, then complete the tile as transparent
        // and raise a visible warning - a silently blank tile on a diagnostic read is
        // indistinguishable from "nothing here", which is the outcome that must not happen.
        const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        const settle = (reason) => {
          endOnce();
          if (!tile.__retried) {
            tile.__retried = true;
            // The retry must not outlive the viewer: navigating away mid-load left this timer
            // firing on a destroyed component, where the computed returns undefined
            // ("this.tileLoadFunction(...) is not a function" in the console, guard 58).
            // Jittered delay: tiles fail in bursts (a saturated PIMS 504s everything in
            // flight), and a fixed 400 ms replays the same burst against the same busy
            // workers. Spreading retries over 400-800 ms lets the server breathe.
            setTimeout(() => {
              if (this._isDestroyed || this._isBeingDestroyed) {
                return;
              }
              this.tileLoadFunction()(tile, src);
            }, 400 + Math.random() * 400);
            return;
          }
          try {
            tile.getImage().src = BLANK;
          } catch (e) {
            // tile disposed while we were failing - nothing left to draw on
          }
          // Guard 58: surface the failure directly. This loader never uses OL's ERROR tile
          // state (see comment above settle), so OL's tileloaderror event can never fire -
          // an event listener for it is dead code, and was: the tile-failure notice had been
          // unreachable since the custom loader landed.
          this.noteTileFailure(reason);
        };
        xhr.addEventListener('load', () => {
          try {
            if (xhr.status < 200 || xhr.status >= 300) {
              settle('http ' + xhr.status);
              return;
            }
            const tileImage = tile.getImage();
            if (!(tileImage instanceof HTMLImageElement)) {
              // disposed tiles can come back as a blank canvas, which never fires 'load'
              endOnce();
              return;
            }
            const url = URL.createObjectURL(xhr.response);
            tileImage.addEventListener('load', () => {
              URL.revokeObjectURL(url);
              tile.__retried = false; // a healthy tile earns back its retry for future loads
              endOnce();
            });
            tileImage.addEventListener('error', () => {
              URL.revokeObjectURL(url);
              settle('decode');
            });
            tileImage.src = url;
          } catch (e) {
            // tile disposed under us (pan/zoom mid-flight): the tile is gone, close the books
            endOnce();
          }
        });
        xhr.addEventListener('error', () => settle('network'));
        xhr.addEventListener('timeout', () => settle('timeout'));
        xhr.addEventListener('abort', () => settle('abort'));
        xhr.send();
      };
    },

    layersToPreload() {
      let layers = [];
      let annot = this.selectedAnnotation || this.routedAnnotation;
      if (annot) {
        layers.push(annot.type === AnnotationType.REVIEWED ? -1 : (annot.username));
      }
      if (this.routedAction === 'review' && !layers.includes(-1)) {
        layers.push(-1);
      }
      return layers;
    },

    overviewCollapsed() {
      return this.overview ? this.overview.getCollapsed() : this.imageWrapper.view.overviewCollapsed;
    },
    // UI-5 (5c): magnification shown with the navigator miniature (same formula as ScaleLine).
    navMagnification() {
      if (!this.image || !this.image.magnification) {
        return null;
      }
      let m = Math.pow(2, this.zoom - this.image.zoom) * this.image.magnification;
      return Math.round(m * 100) / 100;
    },
    // UI-7 B3: red readout when zoomed past the slide's scanned magnification (digital
    // zoom / interpolation) — same threshold as ScaleLine.vue's `interpolation`.
    magnificationOver() {
      return !!(this.image && this.image.magnification && this.zoom > this.image.zoom);
    },
    // UI-5 (5c): physical scale reading for the navigator scale bar (replaces the
    // standalone ScaleLine). 100px-wide bar; label = the physical length it represents.
    navScaleBarPx() {
      return 100;
    },
    navScale() {
      if (!this.image) {
        return null;
      }
      let resolution = (this.image.physicalSizeX ? this.image.physicalSizeX : 1)
        * Math.pow(2, this.image.zoom - this.zoom);
      let length = this.navScaleBarPx * resolution;
      if (this.image.physicalSizeX) {
        let unit = this.$t('um');
        if (length > 1000) {
          length /= 1000;
          unit = this.$t('mm');
        }
        return `${length.toPrecision(3)} ${unit}`;
      }
      return `${Math.round(length * 1000) / 1000} ${this.$t('pixels')}`;
    },
    scaleLineCollapsed() {
      return !this.imageWrapper.view.scaleLineCollapsed;
    },

    correction() {
      return ['correct-add', 'correct-remove'].includes(this.activeEditTool);
    },
    activeSelectInteraction() {
      return this.activeTool === 'select';
    },
    activeDrawInteraction() {
      return !this.activeSelectInteraction || this.correction;
    },
    activeModifyInteraction() {
      return this.activeSelectInteraction && this.activeEditTool && !this.correction;
    },
    idealZoom() {
      let container = this.$refs.container;
      let idealZoom = this.maxZoom;
      let factor = this.maxZoom - this.image.zoom;
      let mapWidth = this.image.width * Math.pow(2, factor);
      let mapHeight = this.image.height * Math.pow(2, factor);
      while (mapWidth > container.clientWidth || mapHeight > container.clientHeight) {
        mapWidth /= 2;
        mapHeight /= 2;
        idealZoom --;
      }
      return idealZoom;
    }
  },
  watch: {
    annotationHiddenTermsKey() {
      this.recomposeAnnotationRaster();
    },

    // A term toggle only recolours what is already fetched, but a LAYER toggle changes which
    // annotations exist in the picture, so it needs a real re-render.
    annotationVisibleUserKey() {
      this.decideAnnotationPlane();
    },

    annotationRasterOpacity(v) {
      if (this._annRasterLayer) {
        this._annRasterLayer.setOpacity(v);
      }
    },

    viewState() {
      this.savePosition();
    },
    segmentationVisible(v) {
      this.syncSegmentationLayer();
      if (!v) {
        this.clearSegHover();
      }
    },
    activeSelectInteraction(v) {
      if (!v) {
        this.clearSegHover(); // hover probe is select-tool only
      }
    },
    segmentationOpacity() {
      this.syncSegmentationLayer();
    },
    overviewCollapsed(value) {
      this.$store.commit(this.imageModule + 'setOverviewCollapsed', value);
    },
    // Whenever a panel flyout opens (from the ☰ menu, the ▶ pipeline button, a
    // keyboard shortcut, or an event), collapse the tool grid and close the ☰ menu
    // so only one surface is open below the toolbar head at a time (design UX).
    activePanel(panel) {
      if (panel) {
        this.toolsExpanded = false;
        this.menuOpen = false;
      }
    }
  },
  methods: {
    setInitialZoom() {
      if (this.zoom !== null) {
        return; // not the first time the viewer is opened => zoom was already initialized
      }
      this.zoom = this.idealZoom;
    },

    async updateMapSize() {
      await this.$nextTick();
      if (this.$refs.map) {
        this.$refs.map.updateSize();
      }
    },

    // ---- viewport annotation raster ---------------------------------------------------
    // AnnotationLayer asks the renderer whether a viewport is small enough to draw as
    // features and refuses to fetch when it is not (image 90752 would ship 95,539,176 bytes
    // and OOM-kill core). That guard alone leaves those annotations invisible, so this draws
    // the server's picture of them instead. It is additive: one image layer on top, removed
    // the moment the renderer says the vector layer can cope again.
    // A write must show immediately. The raster is a picture of the table, so after a
    // create/edit/delete it is stale until something redraws it - and for a delete that means
    // the annotation is still visibly there, which is the worst of the three. Redraw on the
    // viewer's own annotation events, debounced so a burst of edits costs one request.
    onAnnotationWrite() {
      // A create/edit/delete makes every remembered viewport of this image stale.
      if (this._annCache) {
        this._annCache.clear();
      }
      if (!this._annRasterLayer || !this._annLastExtent) {
        return; // vector plane: the feature layer already updated itself
      }
      clearTimeout(this._annWriteTimer);
      this._annWriteTimer = setTimeout(() => {
        this.drawAnnotationRaster(this._annLastExtent, this._annLayerId);
      }, 250);
    },

    // The vector plane already refreshes on a timer (ImsViewer emits `reloadAnnotations`),
    // which is how a second viewer sees another user's write. The raster plane had nothing
    // equivalent: measured, viewer B never saw viewer A's new annotation - 30 s of watching,
    // it only appeared after B changed its view. That breaks "behaves as it does now".
    //
    // Re-rendering every tick would reintroduce exactly the idle drain this work removed, so
    // instead ask the cheap question first: `probe=1` returns headers only - one indexed
    // aggregate, measured **5 ms and 163 bytes** against 165 ms and 119 KB for a render. The
    // fingerprint is count + max(id) + sum(version), which moves on create, on delete, and on
    // edit (Hibernate bumps the row version - verified 0 -> 1 on a geometry drag). Only when
    // it actually moves do we pay for a re-render.
    // Small LRU of already-rendered viewports. Bounded so a long session cannot grow without
    // limit: each entry is one viewport's PNG + index plane, so a dozen is a few MB.
    rememberRaster(key, entry) {
      if (!this._annCache) {
        this._annCache = new Map();
      }
      this._annCache.set(key, entry);
      while (this._annCache.size > 12) {
        this._annCache.delete(this._annCache.keys().next().value);
      }
    },

    // Confirm a cached viewport is still current, cheaply, after showing it. The probe is
    // headers-only (measured 5 ms / 163 bytes) so this is far less work than the render it
    // replaces, and if anything changed we simply re-render.
    async confirmCachedRaster(key, bbox, w, h, users) {
      try {
        const r = await fetch(
          `${window.location.origin}/ims-service/v1/ims/annotation-render/${this.image.id}`
          + `?bbox=${bbox}&width=${w}&height=${h}&users=${users.join(',')}&probe=1`,
          {headers: {Authorization: 'Bearer ' + await this.authToken()}});
        if (!r.ok && r.status !== 204) {
          return;
        }
        const fp = r.headers.get('X-Annotation-Fingerprint');
        const hit = this._annCache && this._annCache.get(key);
        if (hit && fp && hit.fingerprint && fp !== hit.fingerprint) {
          this._annCache.delete(key);
          this.decideAnnotationPlane();
        }
      } catch (e) {
        // a failed confirmation must never disturb what is already on screen
      }
    },

    async onReloadAnnotations(payload) {
      // While the event stream is OPEN, the 10 s timer tick (which arrives with no payload) is
      // redundant: the server pushes fingerprint transitions within ~1 s (SCALE-PLAN P2). The
      // timer keeps firing so that the moment the stream drops, polling resumes unchanged -
      // degraded mode is yesterday's behaviour. Calls WITH a payload (an event, or a hard
      // reload after a write) always proceed.
      if (this._annEventsOpen && !payload) {
        return;
      }
      if (!this._annRasterLayer || !this.image || !this._annLastExtent) {
        return;   // vector plane: AnnotationLayer's own refresh already handles it
      }
      const users = this.annotationVisibleUserIds;
      if (!users) {
        return;
      }
      try {
        const map = this.$refs.map && this.$refs.map.$map;
        if (!map) {
          return;
        }
        const size = map.getSize();
        const w = Math.min(2048, Math.max(16, Math.round(size[0])));
        const h = Math.min(2048, Math.max(16, Math.round(size[1])));
        const bbox = this._annLastExtent.map(v => Math.round(v)).join(',');
        const r = await fetch(
          `${window.location.origin}/ims-service/v1/ims/annotation-render/${this.image.id}`
          + `?bbox=${bbox}&width=${w}&height=${h}&users=${users.join(',')}&probe=1`,
          {headers: {Authorization: 'Bearer ' + await this.authToken()}});
        if (!r.ok && r.status !== 204) {
          return;
        }
        const fp = r.headers.get('X-Annotation-Fingerprint');
        if (fp && this._annFingerprint && fp !== this._annFingerprint) {
          this.decideAnnotationPlane();
        }
      } catch (e) {
        // a failed poll must never disturb what is already on screen
      }
    },

    onAnnotationPlane({plane, extent, layerId, buf}) {
      this._annLastExtent = extent;
      if (plane === 'raster') {
        this.drawAnnotationRaster(extent, layerId, buf);
      } else {
        this.clearAnnotationRaster(layerId);
      }
    },

    // id -> {name, color} for every term in the project's ontology, fetched once. The
    // viewer's style.terms only carries terms it has styled, so hovering an annotation whose
    // term is not in that list produced an empty chip.
    async annotationTermMap() {
      if (this._annTerms) {
        return this._annTerms;
      }
      this._annTerms = {};
      try {
        // The ontology id is not on imageInstance here, and the viewer's style.terms carry
        // ids without names, so neither is usable on its own. Take the project's ontology.
        let oid = this.imageWrapper && this.imageWrapper.imageInstance
          && this.imageWrapper.imageInstance.ontology;
        if (!oid) {
          const pid = this.$route && this.$route.params && this.$route.params.idProject;
          if (pid) {
            const pr = await fetch(`${window.location.origin}/api/project/${pid}.json`,
              {headers: {Authorization: 'Bearer ' + await this.authToken()}});
            if (pr.ok) {
              oid = (await pr.json()).ontology;
            }
          }
        }
        if (oid) {
          const r = await fetch(`${window.location.origin}/api/ontology/${oid}.json`,
            {headers: {Authorization: 'Bearer ' + await this.authToken()}});
          if (r.ok) {
            const j = await r.json();
            // The ontology is a TREE: the UC classes are children of the `UC` term, not
            // top-level entries. Walking only the first level left every UC annotation
            // showing its raw id instead of its term name.
            const walkTerms = (nodes) => {
              (nodes || []).forEach(t => {
                this._annTerms[t.id] = {name: t.name, color: t.color};
                walkTerms(t.children);
              });
            };
            walkTerms(j.children);
          }
        }
      } catch (e) { /* chip falls back to the id */ }
      if (!Object.keys(this._annTerms).length) {
        this._annTerms = null;   // don't cache a failure; try again on the next hover
        return {};
      }
      return this._annTerms;
    },

    async drawAnnotationRaster(extent, layerId, prefetched) {
      const map = this.$refs.map && this.$refs.map.$map;
      if (!map || !this.image || !extent) {
        return;
      }
      try {
        const size = map.getSize();
        const w = Math.min(2048, Math.max(16, Math.round(size[0])));
        const h = Math.min(2048, Math.max(16, Math.round(size[1])));
        const bbox = [extent[0], extent[1], extent[2], extent[3]].map(v => Math.round(v)).join(',');
        // `prefetched` is the body decideAnnotationPlane already received for this exact
        // extent. Redraws that are not driven by a view change (a create/edit/delete, a term
        // toggle) arrive without one and still fetch.
        let buf = prefetched;
        if (!buf) {
          const users = this.annotationVisibleUserIds;
          if (!users) {
            this.clearAnnotationRaster(layerId);
            return;
          }
          // Guard 59: renders race. A pan/zoom or layer toggle can leave several renders in
          // flight, and responses land out of order - a STALE response (older extent, or a
          // layer selection since changed) must never overwrite the fingerprint, or the next
          // probe sees a phantom difference and pays for a redundant render (and anything
          // comparing fingerprints across writes sees values from two different scopes).
          // Last-issued wins: only the newest request may record its fingerprint.
          const seq = this._annFpSeq = (this._annFpSeq || 0) + 1;
          this.onAnnotationLoadingStart();
          let resp;
          try {
            resp = await fetch(
              `${window.location.origin}/ims-service/v1/ims/annotation-render/${this.image.id}`
              + `?bbox=${bbox}&width=${w}&height=${h}&users=${users.join(',')}`,
              {headers: {Authorization: 'Bearer ' + await this.authToken()}});
          } finally {
            this.onAnnotationLoadingEnd();
          }
          if (!resp.ok) {
            this.clearAnnotationRaster(layerId);
            return;
          }
          // Keep the fingerprint in step with what we have just drawn.
          //
          // Only decideAnnotationPlane used to record it, so after a create/edit/delete - which
          // redraws through THIS path - the client still held the pre-write fingerprint.
          // Measured: after creating one annotation the client kept `24433:96085:0` while the
          // server had already moved to `24434:160934:0`, so the next 10 s probe saw a
          // difference that had already been applied and paid for a second, redundant render.
          // One wasted render per write, per open viewer.
          const fp = resp.headers.get('X-Annotation-Fingerprint');
          if (fp && seq === this._annFpSeq) {
            this._annFingerprint = fp;
          }
          buf = await resp.arrayBuffer();
        }
        const dv = new DataView(buf);
        const pngLen = dv.getUint32(0, true);
        const sideLen = dv.getUint32(4, true);
        const idxLen = dv.getUint32(8, true);
        const HDR = 12;
        const png = new Blob([new Uint8Array(buf, HDR, pngLen)], {type: 'image/png'});

        // id + bbox per feature, in viewport pixels: what makes the picture clickable.
        const side = [];
        for (let o = HDR + pngLen; o < HDR + pngLen + sideLen; o += 14) {
          side.push({
            id: dv.getUint32(o, true),
            x0: dv.getUint16(o + 4, true), y0: dv.getUint16(o + 6, true),
            x1: dv.getUint16(o + 8, true), y1: dv.getUint16(o + 10, true),
            termId: dv.getUint16(o + 12, true)
          });
        }
        this._annSidecar = side;

        // Decode the index plane once per viewport. After this a click is a single array
        // lookup: pixel -> 16-bit code -> the annotation that painted it. Exact even where
        // bounding boxes overlap, and no request per click.
        this._annIndex = null;
        if (idxLen > 0) {
          try {
            const bytes = new Uint8Array(buf, HDR + pngLen + sideLen, idxLen);
            const ib = new Blob([bytes], {type: 'image/png'});
            const bmp = await createImageBitmap(ib);
            const cv = document.createElement('canvas');
            cv.width = bmp.width;
            cv.height = bmp.height;
            const ctx = cv.getContext('2d', {willReadFrequently: true});
            ctx.drawImage(bmp, 0, 0);
            const px = ctx.getImageData(0, 0, bmp.width, bmp.height).data;
            const codes = new Uint16Array(bmp.width * bmp.height);
            for (let i = 0, j = 0; i < codes.length; i++, j += 4) {
              codes[i] = (px[j] << 8) | px[j + 1];
            }
            // Store the extent this plane was rendered for. Looking it up by raw viewport
            // pixel is wrong: the raster is requested for the annotation loader's extent,
            // which is not the same rectangle as the current view, so viewport pixels and
            // index pixels do not line up - that is what made a click on one class select
            // another. Map coordinates are unambiguous, so index by those instead.
            const e0 = [extent[0], extent[1], extent[2], extent[3]];
            this._annIndex = {w: bmp.width, h: bmp.height, codes, ext: e0};
            bmp.close();
          } catch (e) {
            this._annIndex = null;
          }
        }

        this._annPngBlob = png;
        const nextUrl = await this.composeAnnotationRaster(png);
        const source = new OlStaticImage({
          url: nextUrl,
          imageExtent: [extent[0], extent[1], extent[2], extent[3]],
          projection: map.getView().getProjection()
        });
        if (!this._annRasterLayer) {
          this._annRasterLayer = new OlImageLayer({source, zIndex: 40});
          map.addLayer(this._annRasterLayer);
        } else {
          this._annRasterLayer.setSource(source);
        }
        // Opacity stays a free client-side gesture. Putting it in the request would turn one
        // drag of the slider into hundreds of re-renders.
        this._annRasterLayer.setOpacity(this.annotationRasterOpacity);
        if (this._annBlobUrl) {
          URL.revokeObjectURL(this._annBlobUrl);
        }
        this._annBlobUrl = nextUrl;
        this._annLayerId = layerId;
      } catch (e) {
        this.clearAnnotationRaster(layerId);
      }
    },

    // Hide the terms the reader has switched off, client-side.
    //
    // Sending the visible terms to the renderer instead would make a free gesture cost a
    // round trip per change - one drag of a filter would be hundreds of re-renders, which is
    // the failure mode the tile-URL approach has in the shipped segmentation overlay. We
    // already know which annotation painted every pixel (the index plane) and each
    // annotation's term (the sidecar), so the mask is a single pass over the image with no
    // request at all.
    async composeAnnotationRaster(pngBlob) {
      const hidden = new Set(
        (((this.imageWrapper || {}).style || {}).terms || [])
          .filter(t => t.visible === false)
          .map(t => t.id)
      );
      if (!hidden.size || !this._annIndex || !(this._annSidecar || []).length) {
        return URL.createObjectURL(pngBlob);     // nothing hidden: use the image as sent
      }
      try {
        const ix = this._annIndex;
        const side = this._annSidecar;
        const bmp = await createImageBitmap(pngBlob);
        const cv = document.createElement('canvas');
        cv.width = bmp.width;
        cv.height = bmp.height;
        const ctx = cv.getContext('2d', {willReadFrequently: true});
        ctx.drawImage(bmp, 0, 0);
        bmp.close();
        const img = ctx.getImageData(0, 0, cv.width, cv.height);
        const px = img.data;
        // index plane and colour plane are the same size and the same shapes
        for (let i = 0, j = 3; i < ix.codes.length && j < px.length; i++, j += 4) {
          const code = ix.codes[i];
          if (code > 0 && code <= side.length && hidden.has(side[code - 1].termId)) {
            px[j] = 0;                            // clear alpha only
          }
        }
        ctx.putImageData(img, 0, 0);
        const out = await new Promise(res => cv.toBlob(res, 'image/png'));
        return URL.createObjectURL(out || pngBlob);
      } catch (e) {
        return URL.createObjectURL(pngBlob);
      }
    },

    // Re-composite when the reader toggles a term. No refetch.
    async recomposeAnnotationRaster() {
      if (!this._annRasterLayer || !this._annPngBlob || !this._annIndex) {
        return;
      }
      const url = await this.composeAnnotationRaster(this._annPngBlob);
      const src = this._annRasterLayer.getSource();
      const ext = src && src.getImageExtent ? src.getImageExtent() : this._annLastExtent;
      this._annRasterLayer.setSource(new OlStaticImage({
        url, imageExtent: ext, projection: this.$refs.map.$map.getView().getProjection()
      }));
      if (this._annBlobUrl) {
        URL.revokeObjectURL(this._annBlobUrl);
      }
      this._annBlobUrl = url;
    },

    clearAnnotationRaster(layerId) {
      const map = this.$refs.map && this.$refs.map.$map;
      if (this._annRasterLayer && map) {
        map.removeLayer(this._annRasterLayer);
      }
      this._annRasterLayer = null;
      this._annSidecar = [];
      // The index plane must go with them. Left behind it keeps answering picks for a
      // viewport that is no longer displayed - after a switch back to the vector plane the
      // sidecar is empty, so every lookup resolved to null and hover died silently.
      this._annIndex = null;
      this._annPngBlob = null;
      this._annLayerId = layerId;
      if (this._annBlobUrl) {
        URL.revokeObjectURL(this._annBlobUrl);
        this._annBlobUrl = null;
      }
    },

    // A click on the raster must select a real annotation, or the picture is a dead end.
    // The sidecar answers locally; otherwise the server resolves the point. Either way this
    // ends in the viewer's ordinary selection path, so the details panel, editing and undo
    // behave exactly as they do on a vector feature.
    // pixel -> annotation, from the index plane. Shared by hover and click so the two can
    // never disagree about what is under the cursor.
    annotationAtCoordinate(coordinate) {
      const ix = this._annIndex;
      const side = this._annSidecar || [];
      if (!ix || !ix.ext || !side.length) {
        return null;
      }
      const e = ix.ext;
      const cx = Math.round((coordinate[0] - e[0]) / (e[2] - e[0]) * ix.w);
      const cy = Math.round((e[3] - coordinate[1]) / (e[3] - e[1]) * ix.h);
      const at = (qx, qy) => (qx < 0 || qy < 0 || qx >= ix.w || qy >= ix.h)
        ? 0 : ix.codes[qy * ix.w + qx];
      let code = at(cx, cy);
      if (!code) {
        const tally = new Map();
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const c = at(cx + dx, cy + dy);
            if (c) {
              tally.set(c, (tally.get(c) || 0) + 1);
            }
          }
        }
        let bestN = 0;
        tally.forEach((n, c) => {
          if (n > bestN) {
            bestN = n;
            code = c;
          }
        });
      }
      return (code > 0 && code <= side.length) ? side[code - 1] : null;
    },

    // True when two DIFFERENT features meet inside the clicked neighbourhood, i.e. the click
    // landed on a boundary and the rasterized answer may not be the geometric one.
    //
    // Measured on UC at half-resolution index: points whose neighbourhood is uniform agree
    // with the server 25/25, points on a boundary only 14/25. So the index plane is exact
    // everywhere except within one index pixel of an edge, and it can tell which case it is
    // in. Boundary clicks defer to the server rather than paying +46 KB on every viewport to
    // ship a full-resolution index.
    //
    // Background is deliberately not ambiguity: a 10-25 px bacillus surrounded by empty space
    // has a non-uniform neighbourhood at every edge pixel, and treating that as ambiguous
    // would send the sputum slide to the server on almost every click.
    pickIsAmbiguous(coordinate) {
      const ix = this._annIndex;
      if (!ix || !ix.ext) {
        return false;
      }
      const e = ix.ext;
      const cx = Math.round((coordinate[0] - e[0]) / (e[2] - e[0]) * ix.w);
      const cy = Math.round((e[3] - coordinate[1]) / (e[3] - e[1]) * ix.h);
      let seen = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const qx = cx + dx;
          const qy = cy + dy;
          if (qx < 0 || qy < 0 || qx >= ix.w || qy >= ix.h) {
            continue;
          }
          const c = ix.codes[qy * ix.w + qx];
          if (c) {
            if (seen && c !== seen) {
              return true;
            }
            seen = c;
          }
        }
      }
      return false;
    },

    // Hover preview. Reads the same index plane the click uses, so it costs no request and
    // cannot disagree with what a click will select. Throttled to one lookup per animation
    // frame, the same budget the shipped segmentation probe uses.
    onMapPointerMove(evt) {
      this.projectedMousePosition = evt.coordinate;
      this.onRasterHover(evt);
    },

    onRasterHover(evt) {
      if (!this._annRasterLayer || evt.dragging) {
        if (this.annotationHover) {
          this.annotationHover = null;
        }
        return;
      }
      if (this._annHoverPending) {
        return;
      }
      this._annHoverPending = true;
      requestAnimationFrame(async () => {
        this._annHoverPending = false;
        const f = this.annotationAtCoordinate(evt.coordinate);
        if (!f) {
          this.annotationHover = null;
          return;
        }
        const map = await this.annotationTermMap();
        const t = map[f.termId] || null;
        // Viewport coordinates, because the chip is position:fixed - it must not depend on
        // some ancestor happening to be positioned.
        const cv = document.querySelector('div.map-container canvas.ol-unselectable');
        const b = cv ? cv.getBoundingClientRect() : {x: 0, y: 0};
        this.annotationHover = {
          id: f.id,
          name: t ? t.name : ('#' + f.id),
          color: t ? t.color : '#666',
          left: Math.round(b.x + evt.pixel[0] + 14),
          top: Math.round(b.y + evt.pixel[1] + 14)
        };
      });
    },

    // Decide raster-vs-vector here, on moveend.
    //
    // This used to hang off AnnotationLayer's loader, which is wrong: once the raster is in
    // force that layer stops fetching, OpenLayers still records the extent as loaded, and the
    // loader never fires again - so zooming from a raster view into an editing view left the
    // raster stuck on and the vector plane never came back. moveend always fires.
    async decideAnnotationPlane() {
      const map = this.$refs.map && this.$refs.map.$map;
      if (!map || !this.image) {
        return;
      }
      if (this._annPlanePending) {
        this._annPlaneAgain = true;
        return;
      }
      this._annPlanePending = true;
      // Tell the vector layer a decision is in flight BEFORE we go and make it.
      //
      // OpenLayers fires its loader on the same moveend that triggers this, and the decision
      // is a round trip - so the loader used to run while `rasterPlane` was still false and
      // fetch vectors for a viewport that was about to become a raster. Measured crossing
      // res 8 -> 16 on sputum: ~2 vector fetches, ~200 KB, immediately discarded.
      this.$eventBus.$emit('annotationPlane', {plane: 'pending'});
      try {
        const ext = map.getView().calculateExtent(map.getSize());
        const bbox = [ext[0], ext[1], ext[2], ext[3]].map(v => Math.round(v)).join(',');
        // Ask for the REAL image, not a 16x16 throwaway.
        //
        // This used to probe at width=16&height=16 purely to learn which plane to use, then
        // fetch the actual raster - two serialized round trips per pan and per zoom step. The
        // probe was not cheap either: measured `query;dur=39.9-72.1 ms`, because deciding
        // "raster" makes the endpoint run the whole geometry query and then render 256 pixels
        // and throw them away. That was 64-95 ms of added latency and one redundant heavy DB
        // query on every view change, which at thousands of concurrent viewers is the more
        // expensive half.
        //
        // The endpoint already answers the question in its status line - 204 means "the
        // vector path is strictly better here" - so the real response IS the decision. One
        // request now does both, and the bytes are the ones we were going to need anyway.
        const size = map.getSize();
        const w = Math.min(2048, Math.max(16, Math.round(size[0])));
        const h = Math.min(2048, Math.max(16, Math.round(size[1])));
        // Reuse a viewport we have already rendered.
        //
        // This is what makes zooming feel like the precomputed pyramid. Measured before:
        // every zoom step re-fetched, INCLUDING returning to a level already visited -
        // res 16 and res 32 were each fetched twice in one six-step sequence, 79 KB and
        // 139 KB again. The pyramid feels instant on the way back purely because its tiles
        // sit in the browser cache.
        //
        // This costs the server NOTHING - it is the reader's own memory and it removes
        // requests rather than adding them, so it does not trade against scale. Staleness is
        // handled by the same fingerprint the refresh poll already uses: the cached entry is
        // shown immediately and a `probe=1` (5 ms, 163 bytes) confirms it in the background,
        // re-rendering only if something actually changed.
        const users = this.annotationVisibleUserIds;
        if (!users) {
          // every annotation layer is hidden (or none is loaded): show nothing, and do not
          // spend a request finding that out.
          this.$eventBus.$emit('annotationPlane', {plane: 'vector', extent: ext, buf: null});
          return;
        }
        const key = `${this.image.id}|${bbox}|${w}x${h}|${users.join(',')}`;
        const hit = this._annCache && this._annCache.get(key);
        if (hit) {
          // Show it now, verify it after.
          this._annCache.delete(key);
          this._annCache.set(key, hit);          // refresh LRU position
          this.annotationsUnavailable = false;
          this.$eventBus.$emit('annotationPlane', {
            plane: hit.plane, extent: ext, buf: hit.buf ? hit.buf.slice(0) : null,
            total: hit.total
          });
          this.confirmCachedRaster(key, bbox, w, h, users);
          return;
        }

        // Guard 59: same last-issued-wins rule as the redraw path - see the comment there.
        const seq = this._annFpSeq = (this._annFpSeq || 0) + 1;
        // "Annotations loading" pill: this render IS the annotations appearing at a new
        // viewport. Selecting from the list centers the view and this fetch takes real time -
        // without the pill the screen read as "nothing happened" (user-reported). The vector
        // plane's bulk loader already drives the same indicator; the raster plane did not.
        this.onAnnotationLoadingStart();
        let r;
        try {
          r = await fetch(
            `${window.location.origin}/ims-service/v1/ims/annotation-render/${this.image.id}`
            + `?bbox=${bbox}&width=${w}&height=${h}&users=${users.join(',')}`,
            {headers: {Authorization: 'Bearer ' + await this.authToken()}});
        } finally {
          this.onAnnotationLoadingEnd();
        }
        // 204 is the renderer DECIDING that vectors are cheaper here - that is a healthy
        // answer and the vector path is the right one. Any other non-ok status means the
        // renderer could not answer at all, and that must take the same guarded path as a
        // thrown fetch.
        //
        // This was the hole: `fetch` does not throw on 502. With ims-service scaled to zero the
        // router returns **502**, `r.ok` was simply false, and the code fell through to
        // `plane: 'vector'` with `annotationsUnavailable = false` - the exact unbounded
        // fallback the guard below exists to prevent. The guard sat in `catch`, which a 502
        // never reaches, so the protection did not work in the one situation it was built for.
        if (!r.ok && r.status !== 204) {
          throw new Error(`annotation renderer unavailable (HTTP ${r.status})`);
        }
        const raster = r.status !== 204;
        this.annotationsUnavailable = false;
        const respFp = r.headers.get('X-Annotation-Fingerprint');
        if (seq === this._annFpSeq) {
          this._annFingerprint = respFp;
        }
        // Remembered so that, if the renderer later becomes unreachable, we know whether
        // falling back to vectors here would be reasonable or ruinous.
        const known = Number(r.headers.get('X-Annotation-Total'));
        if (Number.isFinite(known)) {
          this._annLastTotal = known;
        }
        const body = raster ? await r.arrayBuffer() : null;
        this.rememberRaster(key, {
          plane: raster ? 'raster' : 'vector',
          buf: body ? body.slice(0) : null,
          total: Number(r.headers.get('X-Annotation-Total') || 0),
          fingerprint: respFp // the response's own fingerprint, even when too stale to publish
        });
        this.$eventBus.$emit('annotationPlane', {
          plane: raster ? 'raster' : 'vector',
          extent: ext,
          // Hand the body straight to the painter so it does not fetch the same bytes again.
          buf: body,
          total: Number(r.headers.get('X-Annotation-Total') || 0)
        });
      } catch (e) {
        // Renderer unreachable. Falling back to vectors is today's behaviour and is right for
        // a viewport of ordinary size - but it is unbounded, and on a dense one it is the
        // single heaviest query this platform can make: measured 5.51 MB here against the
        // raster's 0.156 MB, and ~113 MB at the 500k-annotation worst case, which is the load
        // that OOM-kills Core. The renderer being down usually means the cluster is already
        // unhealthy, and that is the worst possible moment for every open viewer to fire that
        // query at once.
        //
        // So: fall back when we have reason to think it is safe, and otherwise say plainly
        // that annotations are unavailable. An empty layer would be indistinguishable from
        // "this slide has no annotations", which is the same silent-blank failure as a tile
        // that never arrives.
        const last = this._annLastTotal;
        if (Number.isFinite(last) && last > ANNOTATION_FALLBACK_MAX) {
          this.annotationsUnavailable = true;
          this.$eventBus.$emit('annotationPlane', {plane: 'unavailable', extent: null});
        } else {
          this.annotationsUnavailable = false;
          this.$eventBus.$emit('annotationPlane', {plane: 'vector', extent: null});
        }
      } finally {
        this._annPlanePending = false;
        if (this._annPlaneAgain) {
          this._annPlaneAgain = false;
          this.decideAnnotationPlane();
        }
      }
    },

    /**
     * Guard 56. Every request this viewer makes outside axios (tiles, renders, picks, the
     * event stream, tile signatures) must get its token HERE, never read the store raw.
     *
     * The axios interceptor refreshes the Keycloak token on every axios call - but the
     * raster viewer's entire hot path is raw fetch/XHR, so an ACTIVELY USED tab could go 30
     * minutes without a single refresh, the token expired mid-session, and every request
     * 401'd until a manual reload ("the viewer suddenly got slow, then broke"). updateToken(70)
     * refreshes only when less than 70 s of validity remain (keycloak-js coalesces concurrent
     * calls), so this costs nothing on the fast path. If the refresh itself fails (the SSO
     * session is gone), fall back to the stored token - requests will 401, which is the
     * truthful outcome; nothing here can loop or throw.
     */
    async authToken() {
      try {
        // Hard 4 s ceiling. Every tile awaits this, so a refresh that never settles (hung
        // Keycloak, throttled background tab, keycloak-js queue wedge) must degrade to "use
        // the current token", never freeze the viewer behind a pending promise.
        const token = await Promise.race([
          updateToken(),
          new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        if (token) {
          this.$store.commit('currentUser/setShortTermToken', token);
          return token;
        }
      } catch (e) {
        // SSO session expired or Keycloak unreachable - use whatever we had
      }
      return this.shortTermToken;
    },

    /**
     * P4 signed tiles: one ACL-checked request per slice per TTL buys every subsequent tile
     * a direct router->PIMS path. 404 = feature off (old Core / no key) - silently keep the
     * ordinary path; that is also the safety net for every failure here. Refreshes at 75%%
     * of the token lifetime so the header never carries an expired token.
     */
    async fetchTileSignature() {
      clearTimeout(this._tileSigTimer);
      if (!this.slices || !this.slices.length) {
        return;
      }
      const sliceId = this.slices[0].id;
      try {
        const r = await fetch(
          `${window.location.origin}/api/sliceinstance/${sliceId}/normalized-tile/signature.json`,
          {headers: {Authorization: 'Bearer ' + await this.authToken()}});
        if (r.status === 404 || r.status === 403) {
          this._tileSig = null;   // feature off (old Core / no key): ordinary path, no retry
          return;
        }
        if (!r.ok) {
          throw new Error(`signature fetch ${r.status}`);
        }
        const data = await r.json();
        if (!data || !data.token) {
          return;
        }
        this._tileSig = data.token;
        this._tileSigSlice = sliceId;
        this._tileSigExp = data.expires;
        const ttlMs = Math.max(60, (data.expires - Date.now() / 1000)) * 1000;
        this._tileSigTimer = setTimeout(() => this.fetchTileSignature(), ttlMs * 0.75);
      } catch (e) {
        // Transient failure: tiles keep flowing through Core; try again in a minute.
        this._tileSig = null;
        this._tileSigTimer = setTimeout(() => this.fetchTileSignature(), 60000);
      }
    },

    /**
     * Guard 57 companion to fetchTileSignature: called on visibilitychange. Only acts when
     * the tab just became visible AND the signature we hold is expired (or about to expire) -
     * i.e. exactly the case where background throttling starved the refresh timer. A live
     * signature is left alone so tab-switching never causes extra signature traffic.
     */
    onVisibilityRefreshSignature() {
      if (document.visibilityState !== 'visible' || !this._tileSig) {
        return;
      }
      if (this._tileSigExp && (this._tileSigExp - Date.now() / 1000) < 60) {
        this.fetchTileSignature();
      }
    },

    /** SCALE-PLAN P2: replace the 10 s poll with a pushed fingerprint.
     *
     * A streamed fetch, not EventSource: EventSource cannot send headers, which would have put
     * the token in the URL and required a router auth change. This carries the same
     * Authorization header as every other call. Each event is the image-wide fingerprint; any
     * transition triggers the SAME path as a changed probe (onReloadAnnotations), so all
     * client-side filters keep applying and the render is still conditional on the viewer's own
     * scoped probe. On any failure the stream closes, _annEventsOpen drops, and the existing
     * 10 s timer takes over - then a retry every 15 s tries to regain the stream. */
    async connectAnnotationEvents() {
      if (this._annEventsCtl || !this.image) {
        return;
      }
      const ctl = new AbortController();
      this._annEventsCtl = ctl;
      try {
        const r = await fetch(
          `${window.location.origin}/ims-service/v1/ims/annotation-events/${this.image.id}`,
          {headers: {Authorization: 'Bearer ' + await this.authToken()}, signal: ctl.signal});
        if (!r.ok || !r.body) {
          throw new Error(`annotation-events unavailable (HTTP ${r.status})`);
        }
        this._annEventsOpen = true;
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let last = null;
        for (;;) {
          const {done, value} = await reader.read();
          if (done) {
            break;
          }
          buf += dec.decode(value, {stream: true});
          let i;
          while ((i = buf.indexOf('\n')) >= 0) {
            const line = buf.slice(0, i).trim();
            buf = buf.slice(i + 1);
            if (line.startsWith('data:')) {
              const fp = line.slice(5).trim();
              if (fp && fp !== last) {
                const first = last === null;
                last = fp;
                // The first frame is the resync snapshot, not a change.
                if (!first) {
                  this.onReloadAnnotations({fromEvents: true});
                }
              }
            }
          }
        }
      } catch (e) {
        // aborted on teardown, or the stream is genuinely unavailable - both end here
      } finally {
        this._annEventsOpen = false;
        this._annEventsCtl = null;
        if (!ctl.signal.aborted) {
          // lost, not closed by us: poll covers the gap; retry the stream periodically
          clearTimeout(this._annEventsRetry);
          this._annEventsRetry = setTimeout(() => this.connectAnnotationEvents(), 15000);
        }
      }
    },

    /** One summary for a selection: metadata + viewport-simplified outline, from
     * /annotation-pick. Shared by the raster click and select-from-list so the two paths
     * cannot drift. `query` is either `id=<id>&x=0&y=0` or `x=<x>&y=<y>&n=1`. Returns the raw
     * pick row or null. The caller marks whatever it builds from it _displayOnly. */
    async pickAnnotationSummary(query) {
      const tol = Math.max(0, this.$refs.map.$map.getView().getResolution() || 0);
      const url = `${window.location.origin}/ims-service/v1/ims/annotation-pick/`
        + `${this.image.id}?${query}&geom=simplified&tol=${tol.toFixed(3)}`;
      const r = await fetch(url, {headers: {Authorization: 'Bearer ' + await this.authToken()}});
      if (!r.ok) {
        return null;
      }
      const d = await r.json();
      return (d.annotations && d.annotations.length && d.annotations[0].location)
        ? d.annotations[0] : null;
    },

    async pickFromRaster(evt) {
      if (!this._annRasterLayer || !this.image) {
        return; // vector plane: OpenLayers' own hit-testing already handled the click
      }
      try {
        let id = null;
        const side = this._annSidecar || [];
        const local = this.annotationAtCoordinate(evt.coordinate);
        if (local && !this.pickIsAmbiguous(evt.coordinate)) {
          id = local.id;
        } else if (local) {
          id = null;   // on a boundary: fall through to the server, which tests real geometry
        } else if (this._annIndex) {
          // The index plane is authoritative about what is drawn. Nothing there means the
          // reader clicked empty background, and the honest response is to deselect - falling
          // through to "nearest annotation" would select something they cannot even see.
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
          return;
        }
        if (id === null && side.length) {
          // The sidecar carries bounding boxes, and a bounding box is only a good proxy for
          // the shape when the shape is small. UC's multipolygons have hugely overlapping
          // boxes, so picking the nearest box selects the wrong annotation. Trust it ONLY
          // when exactly one box contains the click; anything else goes to the server, which
          // tests the real geometry.
          const [px, py] = evt.pixel;
          const inside = side.filter(f => px >= f.x0 && px <= f.x1 && py >= f.y0 && py <= f.y1);
          if (inside.length === 1) {
            id = inside[0].id;
          }
        }
        // ONE cheap pick answers both branches: metadata plus a viewport-simplified outline.
        //
        // This used to be Annotation.fetch(id) - Core's endpoint, which cannot omit geometry,
        // so a click on a UC annotation downloaded 2,967,692 characters of WKT (~2 s on the
        // customer's 12 Mbps link) just to draw a selection outline that is indistinguishable
        // at this zoom from the simplified one. The tolerance is the view resolution: detail
        // finer than one screen pixel, which is the same premise the raster plane rests on.
        const vis = this.annotationVisibleUserIds;
        const a = await this.pickAnnotationSummary(id !== null
          ? `id=${id}&x=0&y=0`
          : `x=${Math.round(evt.coordinate[0])}&y=${Math.round(evt.coordinate[1])}&n=1`
            + (vis ? `&users=${vis.join(',')}` : ''));
        if (!a) {
          return;
        }
        const annot = new Annotation({
          id: a.id,
          // the pick says which table the id came from; a review-layer click must build a
          // ReviewedAnnotation or every downstream type check (review panel, layersToPreload,
          // accept/reject actions) treats it as an ordinary annotation
          class: a.reviewed ? 'be.cytomine.domain.ontology.ReviewedAnnotation'
            : 'be.cytomine.domain.ontology.UserAnnotation',
          project: a.project,
          image: this.image.id,
          slice: a.slice,
          user: a.user,
          location: a.location,
          created: a.created,
          centroid: a.centroid,
          area: a.area,
          areaUnit: a.areaUnit,
          perimeter: a.perimeter,
          perimeterUnit: a.perimeterUnit,
          term: a.terms,
        });
        // Simplified geometry may be LOOKED at, never saved: the model refuses save()/update()
        // while this flag is set, and the edit path swaps in the exact stored geometry first
        // (ModifyInteraction). populate() copies the flag through clone().
        annot._displayOnly = true;
        // Terms/tracks/links come from their own endpoints exactly as before - the same three
        // calls DrawTools makes after any fetch; they fill the TERMS panel (userByTerm) beyond
        // the raw ids the pick already carried.
        await updateTermProperties(annot);
        await updateTrackProperties(annot);
        await updateAnnotationLinkProperties(annot);
        // Materialise before selecting: at raster zoom the vector source is empty, so the
        // ordinary handler would find no feature and the click would look like it did nothing.
        this.$eventBus.$emit('addAnnotation', annot);
        await this.$nextTick();
        this.$eventBus.$emit('selectAnnotationInLayer', {annot, index: this.index});
      } catch (e) {
        // a failed pick must never break the click
      }
    },

    async updateKeyboardInteractions() {
      await this.$refs.map.$createPromise; // wait for ol.Map to be created
      const _m = this.$refs.map.$map;
      if (_m && !this._annPlaneBound) {
        this._annPlaneBound = true;
        this.connectAnnotationEvents();
        // Mark the plane undecided as soon as the view starts changing.
        //
        // OpenLayers runs its own source loader off the same moveend that triggers the
        // decision, and the order between them is not guaranteed - so announcing "pending"
        // from inside decideAnnotationPlane was sometimes too late and the loader fetched
        // vectors for a viewport about to become a raster (measured 452 KB on one step).
        // movestart always precedes the loader.
        // Bind to the VIEW's own change events, not movestart: movestart is an interaction
        // signal and does not fire for a programmatic setResolution/setCenter, so the guard
        // silently did nothing under test - and under test is where it was measured. These
        // fire either way, and always before the source loader runs for the new extent.
        const _pending = () => this.$eventBus.$emit('annotationPlane', {plane: 'pending'});
        _m.getView().on('change:resolution', _pending);
        _m.getView().on('change:center', _pending);
        _m.on('movestart', _pending);
        // moveend, not the vector loader: once the raster is on, the loader stops firing.
        _m.on('moveend', () => this.decideAnnotationPlane());
        _m.on('singleclick', evt => this.pickFromRaster(evt));
        this.decideAnnotationPlane();
      }

      // VR headset only: a controller laser trembles between trigger press and release, and OpenLayers
      // treats >1px of movement as a drag (pan) rather than a click — so taps on annotations/buttons
      // get swallowed. Raise the click vs drag threshold so shaky taps still register as clicks.
      // The handler reads moveTolerance_ live, so mutating it after creation takes effect. Laptop
      // is untouched (gated by isVRBrowser()).
      if (isVRBrowser()) {
        let handler = this.$refs.map.$map.mapBrowserEventHandler_;
        if (handler) {
          handler.moveTolerance_ = 24;
        }
      }

      this.$refs.map.$map.getInteractions().forEach(interaction => {
        if (interaction instanceof KeyboardPan || interaction instanceof KeyboardZoom) {
          interaction.condition_ = (mapBrowserEvent) => {
            return noModifierKeys(mapBrowserEvent)
              && targetNotEditable(mapBrowserEvent)
              && this.isActiveImage
              && !mapBrowserEvent.originalEvent.target.classList.contains('ql-editor');
          };
        } else if (interaction instanceof DragPan) {
          // Navigation: middle (wheel) or right button drag pans at any time; the left button
          // pans only when NOT drawing, so a draw tool owns left-drag (draw the shape). Shift
          // is left to the box-zoom interaction.
          interaction.condition_ = (mapBrowserEvent) => {
            let button = mapBrowserEvent.originalEvent.button;
            if (button === 1 || button === 2) {
              return true;
            }
            return button === 0 && !this.activeDrawInteraction && noModifierKeys(mapBrowserEvent);
          };
        }
      });

      // allow right-button drag panning without the browser context menu over the canvas
      this.$refs.map.$map.getViewport().addEventListener('contextmenu', this.preventContextMenu);
    },
    preventContextMenu(event) {
      event.preventDefault();
    },

    async viewMounted() {
      await this.$refs.view.$createPromise; // wait for ol.View to be created
      if (this.routedAnnotation) {
        this.centerViewOnAnnot(this.routedAnnotation, 500);
      }
      this.savePosition();
    },

    async setBaseSource() {
      await this.$refs.baseSource.$createPromise;
      this.baseSource = this.$refs.baseSource.$source;
      // Guard 58: the "Tiles loading" indicator is deliberately NOT driven from OL tile
      // events. OL never fires tileloadend/tileloaderror for tiles it disposes mid-flight
      // (any pan/zoom during load), so a counter fed from these events leaks upward and the
      // spinner never stops. tileLoadFunction() does its own exactly-once accounting instead.
      this.loadSegmentation(); // UC segmentation raster overlay, if this image has one
    },

    // UC segmentation overlay (spec 2026-06-19). Ask ims-service whether this image has a
    // precomputed raster pyramid; if so, draw it as a TileImage layer on the slide grid. There are
    // no UC vector annotations anymore — the dense segmentation is this raster overlay only.
    async loadSegmentation() {
      if (!this.image || !this.image.id) {
        return;
      }
      try {
        let {data} = await pipelineService.getSegmentation(this.image.id);
        if (data && data.exists) {
          this.segmentation = data;
          this.segmentationHiddenTerms = [];
          this.$nextTick(() => {
            this.syncSegmentationLayer();
            this.initSegmentationProbe();
          });
          return;
        }
      } catch (error) {
        // No overlay / not reachable: leave the viewer untouched (it's optional).
        console.log('segmentation overlay unavailable', error); // eslint-disable-line no-console
      }
      this.segmentation = null;
      this.removeSegmentationLayer();
      this.destroySegmentationProbe();
    },

    // A UC pipeline run finished OR was deleted in the panel — re-sync this image's segmentation
    // overlay if the event is for us (the event bus is global; split viewers each own an image).
    // `expect` says whether the pyramid should now be present (run finished) or absent (run deleted).
    onSegmentationRefresh(payload) {
      if (payload && payload.imageId && this.image && payload.imageId !== this.image.id) {
        return;
      }
      this.reloadSegmentation((payload && payload.expect) || 'present');
    },

    // Re-sync the segmentation overlay (+ class menu) without a page reload, driven by the
    // 'segmentation:refresh' event from PipelinesPanel. ims-service registers/removes the pyramid in
    // a worker tick that can LAG the run's terminal state, so poll until the overlay matches what we
    // expect: 'present' (run finished — wait for it to appear) or 'absent' (run deleted — wait for it
    // to go, then tear down the overlay).
    async reloadSegmentation(expect = 'present', attempt = 0) {
      if (!this.image || !this.image.id) {
        return;
      }
      let exists = false;
      try {
        let {data} = await pipelineService.getSegmentation(this.image.id);
        exists = !!(data && data.exists);
        if (exists) {
          this.segmentation = data;
          this.segmentationHiddenTerms = [];
          this.$nextTick(() => {
            this.syncSegmentationLayer();
            this.initSegmentationProbe();
          });
        } else if (expect === 'absent') {
          this.segmentation = null;
          this.removeSegmentationLayer();
          this.destroySegmentationProbe();
        }
      } catch (error) {
        console.log('segmentation reload failed', error); // eslint-disable-line no-console
        return;
      }
      let satisfied = (expect === 'present') ? exists : !exists;
      if (!satisfied && attempt < SEGMENTATION_RELOAD_MAX_ATTEMPTS) {
        setTimeout(() => this.reloadSegmentation(expect, attempt + 1), SEGMENTATION_RELOAD_INTERVAL_MS);
      }
    },

    // ----- Segmentation hover probe (spec 2026-06-19) -----
    // Tells the user which class is under the cursor (select tool only, this overlay only, and only
    // for currently-shown classes). Uses the label pyramid via SegmentationLabelProbe: O(1) lookups,
    // lazy per-tile fetch, no per-move network. Auth + PNG decode are injected (kept out of the util).
    initSegmentationProbe() {
      this.destroySegmentationProbe();
      if (!this.segmentation || !this.segmentation.exists) {
        return;
      }
      let imageId = this.image.id;
      let version = this.segmentation.version || '';
      this._segProbe = new SegmentationLabelProbe({
        width: this.segmentation.width,
        height: this.segmentation.height,
        resolutions: this.segmentation.resolutions,
        loadTile: (z, x, y) => this.fetchLabelTile(imageId, version, z, x, y),
        onUpdate: () => this.runSegmentationProbe(this._segLastEvt),
      });
      this.bindSegmentationPointer();
    },

    destroySegmentationProbe() {
      if (this._segProbe) {
        this._segProbe.clear();
        this._segProbe = null;
      }
      this.clearSegHover();
    },

    bindSegmentationPointer() {
      let map = this.$refs.map && this.$refs.map.$map;
      if (!map || this._segPointerBound) {
        return;
      }
      map.on('pointermove', this.onSegmentationPointerMove);
      let vp = map.getViewport();
      if (vp) {
        this._segViewport = vp;
        vp.addEventListener('pointerleave', this.clearSegHover);
      }
      this._segPointerBound = true;
    },

    onSegmentationPointerMove(evt) {
      // Coalesce to one probe per animation frame so fast cursor movement never piles up work.
      this._segLastEvt = evt;
      if (this._segRaf) {
        return;
      }
      this._segRaf = window.requestAnimationFrame(() => {
        this._segRaf = null;
        this.runSegmentationProbe(this._segLastEvt);
      });
    },

    runSegmentationProbe(evt) {
      if (!evt || !this._segProbe || !this.segmentation || !this.segmentation.exists
          || !this.segmentationVisible || !this.activeSelectInteraction || evt.dragging) {
        this.clearSegHover();
        return;
      }
      let map = this.$refs.map && this.$refs.map.$map;
      let grid = this.baseSource && this.baseSource.getTileGrid();
      if (!map || !grid) {
        this.clearSegHover();
        return;
      }
      let coord = evt.coordinate;
      let z = grid.getZForResolution(map.getView().getResolution());
      let result = this._segProbe.probe(coord[0], coord[1], z);
      if (result.status === 'ready') {
        let cls = this.segmentation.classes.find(c => c.index === result.index);
        if (cls && cls.term_id != null && !this.segmentationHiddenTerms.includes(cls.term_id)) {
          let oe = evt.originalEvent || {};
          this.segHover = {name: cls.name, color: cls.color || '#00aa50', x: oe.clientX || 0, y: oe.clientY || 0};
          return;
        }
      }
      this.clearSegHover();
    },

    clearSegHover() {
      if (this.segHover) {
        this.segHover = null;
      }
    },

    // Fetch + decode one label tile -> 256*256 class-index byte array (R channel), or null if empty.
    async fetchLabelTile(imageId, version, z, x, y) {
      const token = await this.authToken();
      return new Promise(resolve => {
        let q = new URLSearchParams({format: 'label', v: version});
        let url = `${window.location.origin}/ims-service/v1/ims/segmentation-tiles/${imageId}/${z}/${x}/${y}.png?${q.toString()}`;
        let xhr = new XMLHttpRequest();
        let tenant = (constants.ACTIVE_TENANT || '').toString().trim().toLowerCase();
        xhr.responseType = 'blob';
        xhr.open('GET', url);
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        if (tenant) {
          xhr.setRequestHeader('X-TENANT', tenant);
        }
        xhr.onerror = () => resolve(null);
        xhr.onload = () => {
          if (xhr.status === 204 || !xhr.response || xhr.response.size === 0) {
            resolve(null);
            return;
          }
          let img = new Image();
          let blobUrl = URL.createObjectURL(xhr.response);
          img.onload = () => {
            URL.revokeObjectURL(blobUrl);
            if (img.naturalWidth !== 256 || img.naturalHeight !== 256) {
              resolve(null);
              return;
            }
            let cv = this._labelCanvas || (this._labelCanvas = document.createElement('canvas'));
            cv.width = 256;
            cv.height = 256;
            let cx = cv.getContext('2d', {willReadFrequently: true});
            cx.clearRect(0, 0, 256, 256);
            cx.drawImage(img, 0, 0);
            let data = cx.getImageData(0, 0, 256, 256).data;
            let arr = new Uint8Array(256 * 256);
            for (let i = 0; i < arr.length; i++) {
              arr[i] = data[i * 4]; // R channel = class index (label PNG is greyscale)
            }
            resolve(arr);
          };
          img.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            resolve(null);
          };
          img.src = blobUrl;
        };
        xhr.send();
      });
    },

    // The set of class term_ids currently shown (all classes minus the toggled-off ones).
    visibleSegmentationTermIds() {
      if (!this.segmentation || !this.segmentation.classes) {
        return [];
      }
      return this.segmentation.classes
        .map(c => c.term_id)
        .filter(id => id != null && !this.segmentationHiddenTerms.includes(id));
    },

    removeSegmentationLayer() {
      let map = this.$refs.map && this.$refs.map.$map;
      if (map && this._segLayer) {
        map.removeLayer(this._segLayer);
      }
      this._segLayer = null;
    },

    // Reconcile the single segmentation TileImage layer with the current toggle/opacity/term filter.
    // Opacity + term filter live in the tile URL, so a change rebuilds the source (cheap; tiles are
    // cached server-side and in the browser per distinct URL).
    syncSegmentationLayer() {
      let map = this.$refs.map && this.$refs.map.$map;
      if (!map || !this.baseSource || !this.segmentation) {
        return;
      }
      this.removeSegmentationLayer();
      if (!this.segmentationVisible) {
        return;
      }
      let grid = this.baseSource.getTileGrid();
      if (!grid) {
        return;
      }
      let imageId = this.image.id;
      let opacity = this.segmentationOpacity;
      let version = this.segmentation.version || '';
      let allClasses = (this.segmentation.classes || []).filter(c => c.term_id != null);
      let visibleIds = this.visibleSegmentationTermIds();
      // Only send `terms` when some are hidden — otherwise serve all (smaller, more cacheable URL).
      let termsParam = (visibleIds.length && visibleIds.length < allClasses.length)
        ? visibleIds.join(',') : '';
      // TileImage on the slide's own grid: the OL tile coord (z,x,y) maps 1:1 to the pyramid tile
      // (the precompute used this exact grid: origin [0,H], res [2ⁿ…1], 256), so tiles register.
      let imageHeight = this.segmentation.height || (this.image && this.image.height);
      let source = new TileImageSource({
        projection: this.projectionName,
        tileGrid: grid,
        tileUrlFunction: coord => {
          // Derive the pyramid tile (z,x,y) from the tile's WORLD extent — the exact inverse of how
          // the pyramid was built (origin [0,H], maxY at row 0). This is independent of OpenLayers'
          // internal tile-coord axis/sign (its y can be negative for this grid), so tiles register.
          let ext = grid.getTileCoordExtent(coord); // [minX, minY, maxX, maxY] in image coords
          let span = ext[2] - ext[0];                // tile world size = 256 * resolution[z]
          let z = coord[0];
          let x = Math.round(ext[0] / span);
          let y = Math.round((imageHeight - ext[3]) / span);
          let q = new URLSearchParams({opacity: opacity, v: version});
          if (termsParam) {
            q.set('terms', termsParam);
          }
          return `${window.location.origin}/ims-service/v1/ims/segmentation-tiles/${imageId}/${z}/${x}/${y}.png?${q.toString()}`;
        },
        tileLoadFunction: this.tileLoadFunction, // reuse Bearer + X-TENANT XHR loader
      });
      let olLayer = new TileLayer({source});
      map.addLayer(olLayer);
      this._segLayer = olLayer;
    },

    toggleSegmentationTerm(termId) {
      if (termId == null) {
        return;
      }
      let idx = this.segmentationHiddenTerms.indexOf(termId);
      if (idx >= 0) {
        this.segmentationHiddenTerms.splice(idx, 1);
      } else {
        this.segmentationHiddenTerms.push(termId);
      }
      this.syncSegmentationLayer();
    },

    /* Dock layout: ☰-menu entries that toggle the left-docked Images tray (viewer-level)
       and Annotation list (this image). The map viewport changes width, so OpenLayers must
       recompute its size after the DOM settles. */
    isDockOpen(id) {
      if (id === 'images-dock') {
        const v = this.$store.getters['currentProject/currentViewer'];
        return !!(v && v.imageSelector);
      }
      return !!(this.imageWrapper.annotationsList && this.imageWrapper.annotationsList.open);
    },
    toggleDock(id) {
      if (id === 'images-dock') {
        const viewerModule = this.$store.getters['currentProject/currentViewerModule'];
        this.$store.commit(viewerModule + 'setImageSelector', !this.isDockOpen(id));
      } else {
        this.$store.commit(this.imageModule + 'setShowAnnotationsList', !this.isDockOpen(id));
      }
      this.menuOpen = false;
      this.$nextTick(() => this.$eventBus.$emit('updateMapSize'));
    },

    onTileLoadStart() {
      this.tileLoadingCount++;
      clearTimeout(this.tilesHideTimer);
      this.tilesLoading = true;
    },
    // A failed tile must be SEEN: a silently blank tile on a diagnostic read is
    // indistinguishable from "nothing here". Called by tileLoadFunction's settle() after the
    // retry is exhausted (guard 58: the OL tileloaderror event can never fire with the custom
    // loader, so this is the only feed). Counter accounting is the loader's job, not ours.
    noteTileFailure() {
      this.tileErrorCount++;
      clearTimeout(this.tileErrorTimer);
      // Tiles fail in bursts - a lost connection kills every tile in flight. Clear the notice
      // only after a quiet period, so it does not flicker once per tile.
      this.tileErrorTimer = setTimeout(() => {
        this.tileErrorCount = 0;
      }, 8000);
    },
    onTileLoadEnd() {
      this.tileLoadingCount = Math.max(0, this.tileLoadingCount - 1);
      if (this.tileLoadingCount === 0) {
        clearTimeout(this.tilesHideTimer);
        this.tilesHideTimer = setTimeout(() => {
          this.tilesLoading = false;
        }, 250);
      }
    },
    onAnnotationLoadingStart() {
      this.annotationLoadingCount++;
      clearTimeout(this.annotsHideTimer);
      this.annotationsLoading = true;
    },
    onAnnotationLoadingEnd() {
      this.annotationLoadingCount = Math.max(0, this.annotationLoadingCount - 1);
      if (this.annotationLoadingCount === 0) {
        clearTimeout(this.annotsHideTimer);
        this.annotsHideTimer = setTimeout(() => {
          this.annotationsLoading = false;
        }, 250);
      }
    },
    onLiveDrawSize(size) {
      this.liveDrawSize = size;
    },

    // VR controller face button → switch the ACTIVE viewer image to the Select tool.
    onVrSelectShortcut() {
      if (this.isActiveImage) {
        this.$store.dispatch(this.imageModule + 'activateTool', 'select');
      }
    },

    async addOverviewMap() {
      if (!this.isPanelDisplayed('overview')) {
        return;
      }

      await this.$refs.map.$createPromise; // wait for ol.Map to be created
      await this.$refs.baseLayer.$createPromise; // wait for ol.Layer to be created
      // ...and for the SOURCE, which vuelayers creates after the layer. Without this the
      // overview was built with a null source and rendered 100% transparent - the same empty
      // box as before, just a different cause.
      await this.$refs.baseSource.$createPromise;

      let map = this.$refs.map.$map;

      // A LAYER INSTANCE CAN ONLY BELONG TO ONE MAP. Passing the main map's own layer object
      // here put the same instance in two maps at once, and the overview rendered solid black
      // - measured: 3,600 of 3,600 sampled canvas pixels were black, zero transparent, zero
      // anything else. Give the overview its own layer over the SAME source: the source (and
      // therefore the tile cache and every network request) is shared, so this costs no extra
      // fetching - only a second, cheap layer object.
      this.overview = new OverviewMap({
        view: new View({projection: this.projectionName}),
        layers: [new TileLayer({
          source: this.$refs.baseSource.$source,
          extent: this.extent
        })],
        tipLabel: this.$t('overview'),
        target: this.$refs.overview,
        collapsed: this.imageWrapper.view.overviewCollapsed
      });
      map.addControl(this.overview);

      // The overview view was created with a projection and nothing else, so it had no idea
      // how big the slide is: it rendered at some default resolution showing a sliver of one
      // corner, with black everywhere else. That - not the shared layer - is what the black
      // box actually was. Fit it to the image extent once the control has been added and has
      // a size. `constrainResolution: false` because the slide's pyramid resolutions are
      // powers of two and the fitted overview resolution is not one of them.
      this.$nextTick(() => {
        const ovMap = this.overview.getOverviewMap();
        ovMap.updateSize();
        const size = ovMap.getSize();
        if (size && size[0] > 0 && size[1] > 0) {
          ovMap.getView().fit(this.extent, {size, constrainResolution: false});
        }
      });

      this.overview.getOverviewMap().on(('click'), (evt) => {
        let size = map.getSize();
        map.getView().centerOn(evt.coordinate, size, [size[0] / 2, size[1] / 2]);
      });
    },

    toggleOverview() {
      if (this.overview) {
        this.overview.setCollapsed(!this.imageWrapper.view.overviewCollapsed);
      }
    },

    togglePanel(panel) {
      this.$store.commit(this.imageModule + 'togglePanel', panel);
    },
    closeActivePanel() {
      if (this.activePanel) {
        this.$store.commit(this.imageModule + 'togglePanel', this.activePanel);
      }
    },
    // Unified toolbar — the ☰ menu, an open panel flyout, and the expanded tool
    // grid are mutually exclusive (design CanvasToolbar behaviour): opening any one
    // closes the other two, and the flyouts/menu drop down below the collapsed head.
    toggleMenu() {
      if (this.menuOpen) {
        this.menuOpen = false;
      } else {
        this.menuOpen = true;
        this.toolsExpanded = false;
        this.closeActivePanel();
      }
    },
    // UI-6: the ☰ button doubles as the expand control when the flyout is collapsed.
    onMenuButton() {
      if (this.flyoutCollapsed) {
        this.flyoutCollapsed = false;
        return;
      }
      this.toggleMenu();
    },
    // UI-6: collapse the entire flyout (menu, tool grid, open panel) to the floating ☰ icon.
    collapseFlyout() {
      this.menuOpen = false;
      this.toolsExpanded = false;
      this.flyoutCollapsed = true;
    },
    toggleTools() {
      if (this.toolsExpanded) {
        this.toolsExpanded = false;
      } else {
        this.toolsExpanded = true;
        this.menuOpen = false;
        this.closeActivePanel();
      }
    },
    // pick a panel from the ☰ menu / ▶ pipeline → open its flyout, close menu + grid
    openPanelFromMenu(panel) {
      this.menuOpen = false;
      this.toolsExpanded = false;
      this.togglePanel(panel);
    },
    // UI-7 B9: toggle the browser into/out of full-screen (whole document).
    toggleFullscreen() {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      } else {
        let el = document.documentElement;
        if (el.requestFullscreen) {
          el.requestFullscreen().catch(() => {});
        }
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },

    savePosition: _.debounce(async function () {
      if (this.$refs.view) {
        let extent = this.$refs.view.$view.calculateExtent(); // [minX, minY, maxX, maxY]

        // Do not re-post a position that has not moved.
        //
        // This method re-arms itself every SAVE_POSITION_IN_IMAGE_INTERVAL, so a viewer left
        // open on a desk kept writing the same rectangle forever - and each write is a Mongo
        // insert plus an ACL check that reads Postgres. Measured on one idle viewer: 19 posts
        // in 8 minutes doing nothing at all.
        //
        // Broadcasting is the exception: when this user is being followed, the heartbeat is the
        // signal that they are still there, so it keeps ticking.
        const broadcast = this.imageWrapper.tracking.broadcast;
        const fingerprint = [
          Math.round(extent[0]), Math.round(extent[1]),
          Math.round(extent[2]), Math.round(extent[3]),
          this.zoom, this.rotation,
        ].join(',');
        if (!broadcast && fingerprint === this._lastPositionSaved) {
          clearTimeout(this.timeoutSavePosition);
          this.timeoutSavePosition =
            setTimeout(this.savePosition, constants.SAVE_POSITION_IN_IMAGE_INTERVAL);
          return;
        }

        try {
          await UserPosition.create({
            image: this.image.id,
            slice: this.slices[0].id,
            zoom: this.zoom,
            rotation: this.rotation,
            bottomLeftX: Math.round(extent[0]),
            bottomLeftY: Math.round(extent[1]),
            bottomRightX: Math.round(extent[2]),
            bottomRightY: Math.round(extent[1]),
            topLeftX: Math.round(extent[0]),
            topLeftY: Math.round(extent[3]),
            topRightX: Math.round(extent[2]),
            topRightY: Math.round(extent[3]),
            broadcast: broadcast
          });
          this._lastPositionSaved = fingerprint;
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-save-user-position')});
        }

        clearTimeout(this.timeoutSavePosition);
        this.timeoutSavePosition = setTimeout(this.savePosition, constants.SAVE_POSITION_IN_IMAGE_INTERVAL);
      }
    }, 500),

    fitZoom() {
      this.$refs.view.animate({
        zoom: this.idealZoom,
        center: [this.image.width / 2, this.image.height / 2]
      });
    },

    async centerViewOnAnnot(annot, duration) {
      if (annot.image === this.image.id) {
        if (!annot.location) {
          //in case annotation location has not been loaded
          annot = (await Ims.instance.api.get(`/annotations/${annot.id}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        }

        if (annot.project !== this.project.id) {
          await this.$router.push(`/project/${annot.project}/image/${annot.image}/annotation/${annot.id}`);
        }

        let geometry = this.format.readGeometry(annot.location);
        await this.$refs.view.fit(geometry, {duration, padding: [10, 10, 10, 10], maxZoom: this.image.zoom});

        if (!Object.prototype.hasOwnProperty.call(annot, 'centroid')) {
          return;
        }

        // HACK: center set by view.fit() is incorrect => reset it manually
        this.center = (geometry.getType() === 'Point') ? geometry.getFirstCoordinate()
          : [annot.centroid.x, annot.centroid.y];
        // ---
      }
    },

    async selectAnnotationHandler({index, annot, center = false, showComments = false}) {
      if (this.index === index && annot.image === this.image.id) {
        try {
          let sliceChange = false;
          if (!annot.slice || !annot.location) {
            // List rows arrive WITHOUT geometry now (showWKT:false - a UC page was 8 MB with
            // it). Enrich through the same summary the raster click uses: simplified outline
            // plus slice/centroid, marked display-only so the save guard applies. Fall back to
            // the old full fetch if the summary fails - degraded mode is yesterday's behaviour.
            const a = await this.pickAnnotationSummary(`id=${annot.id}&x=0&y=0`);
            if (a) {
              annot.location = annot.location || a.location;
              annot.slice = annot.slice || a.slice;
              annot.centroid = annot.centroid || a.centroid;
              annot._displayOnly = true;
            } else {
              annot = await Annotation.fetch(annot.id);
            }
          }

          if (!this.sliceIds.includes(annot.slice)) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
            this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id, hard: true});
            sliceChange = true;
          }

          if (showComments) {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }

          this.selectedAnnotation = annot; // used to pre-load annot layer
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
          this.$eventBus.$emit('selectAnnotationInLayer', {index, annot});

          if (center) {
            await this.viewMounted();
            let duration = (sliceChange) ? undefined : 500;
            this.centerViewOnAnnot(annot, duration);
          }
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    },

    isPanelDisplayed(panel) {
      return this.configUI[`project-explore-${panel}`];
    },
    shortkeyHandler(key) {
      if (!key.startsWith('toggle-all-') && !this.isActiveImage) { // shortkey should only be applied to active map
        return;
      }

      key = key.replace('toggle-all-', 'toggle-');
      switch (key) {
        case 'toggle-information':
          if (this.isPanelDisplayed('info')) {
            this.togglePanel('info');
          }
          return;
        case 'toggle-zoom':
          if (this.isPanelDisplayed('digital-zoom')) {
            this.togglePanel('digital-zoom');
          }
          return;
        case 'toggle-link':
          if (this.isPanelDisplayed('link') && this.nbImages > 1) {
            this.togglePanel('link');
          }
          return;
        case 'toggle-filters':
          if (this.isPanelDisplayed('color-manipulation')) {
            this.togglePanel('colors');
          }
          return;
        case 'toggle-layers':
          if (this.isPanelDisplayed('image-layers')) {
            this.togglePanel('layers');
          }
          return;
        case 'toggle-ontology':
          if (this.isPanelDisplayed('ontology') && this.terms && this.terms.length > 0) {
            this.togglePanel('ontology');
          }
          return;
        case 'toggle-properties':
          if (this.isPanelDisplayed('property')) {
            this.togglePanel('properties');
          }
          return;
        case 'toggle-broadcast':
          if (this.isPanelDisplayed('follow')) {
            this.togglePanel('follow');
          }
          return;
        case 'toggle-review':
          if (this.isPanelDisplayed('review') && this.canEdit) {
            this.togglePanel('review');
          }
          return;
        case 'toggle-overview':
          if (this.isPanelDisplayed('overview')) {
            this.toggleOverview();
          }
          return;
      }
    },
    async takeScreenshot() {
      // Use of css percent values and html2canvas results in strange behavior
      // Set image container as actual height in pixel (not in percent) to avoid image distortion when retrieving canvas
      let containerHeight = document.querySelector('.map-container').clientHeight;
      document.querySelector('.map-container').style.height = containerHeight + 'px';

      let a = document.createElement('a');
      a.href = await this.$html2canvas(document.querySelector('.ol-unselectable'), {type: 'dataURL'});
      let imageName = 'image_' + this.image.id.toString() + '_project_' + this.image.project.toString() + '.png';
      a.download = imageName;
      a.click();

      // Reset container css values as previous
      document.querySelector('.map-container').style.height = '';
    },
  },
  async created() {
    this._tileSig = null;
    this._tileSigSlice = null;
    this._tileSigExp = null;
    this.fetchTileSignature();
    if (!getProj(this.projectionName)) { // if image opened for the first time
      let projection = createProj({code: this.projectionName, units: 'pixels', extent: this.extent});
      addProj(projection);
    }

    if (this.routedAction === 'review') {
      this.togglePanel('review');
      if (!this.image.inReview) {
        try {
          let clone = await this.image.clone().review();
          this.$store.commit(this.imageModule + 'setImageInstance', clone);
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-start-review')});
        }
      }
      this.$store.commit(this.imageModule + 'setReviewMode', true);
    }

    // remove all selected features in order to reselect them when they will be added to the map (otherwise,
    // issue with the select interaction)
    this.selectedLayers.forEach(layer => {
      this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', {layer, cache: true});
    });

    let annot = this.imageWrapper.routedAnnotation;
    if (!annot) {
      let idRoutedAnnot = this.$route.params.idAnnotation;
      if (idRoutedAnnot) {
        try {
          annot = (await Ims.instance.api.get(`/annotations/${idRoutedAnnot}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    }

    if (annot) {
      if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
        let response = await Ims.instance.api.get(`/annotation-layers/${annot.annotationLayer}/task-run-layer`);
        let taskRunLayer = response.data;
        annot.image = taskRunLayer.image;
      }

      try {
        if (annot.image === this.image.id) {
          if (!this.sliceIds.includes(annot.slice) && Object.prototype.hasOwnProperty.call(annot, 'slice')) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
          }
          this.routedAnnotation = annot;
          if (this.routedAction === 'comments') {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        }

        this.$store.commit(this.imageModule + 'clearRoutedAnnotation');
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
      }
    }

    try {
      await new ImageConsultation({image: this.image.id}).save();
    } catch (error) {
      console.log(error);
      this.$notify({type: 'error', text: this.$t('notif-error-save-image-consultation')});
    }

    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('editAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('deleteAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$on('reloadAnnotations', this.onReloadAnnotations);
    this.$eventBus.$on('updateMapSize', this.updateMapSize);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$on('close-metadata', () => this.$store.commit(this.imageModule + 'togglePanel', 'info'));
    this.$eventBus.$on('annotationLoadingStart', this.onAnnotationLoadingStart);
    this.$eventBus.$on('annotationLoadingEnd', this.onAnnotationLoadingEnd);
    this.$eventBus.$on('liveDrawSize', this.onLiveDrawSize);
    this.$eventBus.$on('vr-shortcut-select', this.onVrSelectShortcut);
    this.$eventBus.$on('segmentation:refresh', this.onSegmentationRefresh);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    // Guard 57. The signature refresh timer is throttled/frozen while the tab is backgrounded
    // (Chrome intensive throttling / tab freezing), so a user returning after a long absence
    // would ride the slower Core fallback path until the stale timer finally fired. Expired
    // signatures are harmless (the router falls back to Core, which re-authorizes), but
    // recovery should be immediate: re-sign the instant the tab becomes visible again.
    document.addEventListener('visibilitychange', this.onVisibilityRefreshSignature);
    this.setInitialZoom();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityRefreshSignature);
    clearTimeout(this._tileSigTimer);
    clearTimeout(this._annEventsRetry);
    if (this._annEventsCtl) {
      this._annEventsCtl.abort();
    }
    this.$eventBus.$off('addAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('editAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('deleteAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$off('reloadAnnotations', this.onReloadAnnotations);
    this.clearAnnotationRaster();
    this.removeSegmentationLayer(); // UC segmentation raster overlay
    // tear down the segmentation hover probe
    if (this._segRaf) {
      window.cancelAnimationFrame(this._segRaf);
      this._segRaf = null;
    }
    let segMap = this.$refs.map && this.$refs.map.$map;
    if (segMap && this._segPointerBound) {
      segMap.un('pointermove', this.onSegmentationPointerMove);
    }
    if (this._segViewport) {
      this._segViewport.removeEventListener('pointerleave', this.clearSegHover);
      this._segViewport = null;
    }
    this._segPointerBound = false;
    this.destroySegmentationProbe();
    this.$eventBus.$off('updateMapSize', this.updateMapSize);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$off('close-metadata');
    this.$eventBus.$off('annotationLoadingStart', this.onAnnotationLoadingStart);
    this.$eventBus.$off('annotationLoadingEnd', this.onAnnotationLoadingEnd);
    this.$eventBus.$off('liveDrawSize', this.onLiveDrawSize);
    this.$eventBus.$off('vr-shortcut-select', this.onVrSelectShortcut);
    this.$eventBus.$off('segmentation:refresh', this.onSegmentationRefresh);
    clearTimeout(this.tilesHideTimer);
    clearTimeout(this.annotsHideTimer);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    clearTimeout(this.timeoutSavePosition);
  }
};
</script>

<style lang="scss">
.annotation-hover-chip {
  position: fixed;
  z-index: 3000;
  pointer-events: none;
  background: rgba(20, 20, 20, 0.88);
  color: #fff;
  font-size: 0.78rem;
  line-height: 1.1;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.annotation-hover-chip .swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  margin-right: 6px;
}

@import '~vuelayers/lib/style.css';

/* UI-5 (5b/5e): ZayaAI light panel rail + flyout-card panels */
$backgroundPanelBar: #ffffff;
$widthPanelBar: 2.8rem;
$backgroundPanel: #ffffff;
$colorPanelLink: #5a6172;
$colorHoverPanelLink: #1e63d0;
$colorBorderPanelLink: #e5e8ee;
$colorOpenedPanelLink: #1e63d0;

.map-container {
  display:flex;
  position: relative;
  width: 100%;
  height: 100%;
}

/* Dock layout: the map and ALL its overlays live here, so absolute-positioned widgets
   (toolbar, overview, notices) anchor to the visible map area - the docked annotations
   column sits beside it, never under it. */
.map-body {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

/* live width × height while drawing a rectangle (fixed top-left) */
.live-draw-size {
  position: absolute;
  top: 0.7em;
  left: 0.6rem;
  z-index: 31;
  display: flex;
  align-items: baseline;
  gap: 6px;
  background: rgba(15, 23, 42, 0.86);
  color: #fff;
  border-radius: 9px;
  padding: 6px 11px;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(15, 17, 22, 0.22);
  pointer-events: none;
  font-variant-numeric: tabular-nums;
}
.live-draw-size .lds-k {
  color: #9bbcf0;
  font-weight: 700;
  margin-right: 3px;
}
.live-draw-size .lds-x {
  color: #94a3b8;
  font-weight: 500;
}
.live-draw-size .lds-unit {
  color: #cbd5e1;
  font-weight: 600;
  font-size: 11.5px;
  margin-left: 2px;
}

.map {
  flex-grow: 1;
}

/* UI-5 (5e): draw-tool card floats top-RIGHT, clear of the annotation-details
   panel (which opens top-left). */
/* UI: unified CanvasToolbar (design .canvas-toolbar) — ONE floating bar, top-left,
   holding the ☰ panels menu, the ▶ pipeline button, and the active-tool summary +
   expandable draw-tools grid. The opened panels render as flyout cards anchored to it. */
.canvas-toolbar {
  position: absolute;
  top: 0.7em;
  left: 0.6rem;
  z-index: 30;
  background: #fff;
  border: 1px solid $colorBorderPanelLink;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 17, 22, 0.14), 0 2px 6px rgba(15, 17, 22, 0.06);
  /* UI-6 fix: size to the head content so the extra collapse button never overflows/overlaps
     the close button; keep a sensible floor (for the draw-tools grid) and ceiling. */
  width: max-content;
  min-width: 236px;
  max-width: 320px;
}

.canvas-toolbar.collapsed {
  width: auto;
}

/* UI-6: collapsed flyout — everything hides except the primary ☰ button (its tooltip
   becomes "show"); clicking ☰ re-expands to the previous state. */
.canvas-toolbar.flyout-collapsed {
  width: auto;
  min-width: 0;       /* override the 236px floor so it shrinks to just the ☰ button */
  max-width: none;
}
.canvas-toolbar.flyout-collapsed .ct-head > *:not(.ct-menu-primary) {
  display: none;
}
.canvas-toolbar.flyout-collapsed .ct-grid,
.canvas-toolbar.flyout-collapsed .menu-popover,
.canvas-toolbar.flyout-collapsed .panel-options,
.canvas-toolbar.flyout-collapsed .panel-options-wide,
.canvas-toolbar.flyout-collapsed .panel-metadata {
  display: none !important;
}

.ct-head {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
}

.ct-divider {
  width: 1px;
  height: 20px;
  background: $colorBorderPanelLink;
  flex: 0 0 1px;
  margin: 0 2px;
}

.ct-menu {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0e1116;
  cursor: pointer;
  flex: 0 0 30px;
  background: transparent;
  border: 0;
  padding: 0;
  font-size: 0.95rem;
}

.ct-menu:hover {
  background: #f2f4f8;
}

.ct-menu.open {
  background: #e4ecfb;
  color: #17509e;
}

.ct-close {
  color: #8a91a1;
}

.ct-close:hover {
  background: #fbeae9;
  color: #c8443c;
}

.ct-tool-body {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  cursor: pointer;
  border-radius: 6px;
  padding: 0 4px;
  min-width: 0;
}

.ct-tool-body:hover {
  background: #fafbfc;
}

.ct-active {
  width: 26px;
  height: 26px;
  border-radius: 5px;
  background: #1e63d0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  font-size: 0.8rem;
}

.ct-label {
  flex: 1;
  font-size: 12.5px;
  font-weight: 600;
  color: #0e1116;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.canvas-toolbar.collapsed .ct-label {
  display: none;
}

.ct-caret {
  color: #8a91a1;
  width: 20px;
  text-align: center;
  flex: 0 0 20px;
}

.ct-grid {
  border-top: 1px solid $colorBorderPanelLink;
  padding: 4px 6px 8px;
}

/* panels menu popover (design .menu-popover) — opens below the toolbar head */
.menu-popover {
  position: absolute;
  top: 100%;
  margin-top: 6px;
  left: 0;
  width: 226px;
  background: #fff;
  border: 1px solid $colorBorderPanelLink;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 17, 22, 0.14), 0 2px 6px rgba(15, 17, 22, 0.06);
  z-index: 13;
  padding: 6px;
}

.menu-sep {
  height: 1px;
  background: $colorBorderPanelLink;
  margin: 6px 4px;
}

.grp-h {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #8a91a1;
  text-transform: uppercase;
  padding: 6px 10px 4px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12.5px;
  color: #0e1116;
  cursor: pointer;
  font-weight: 500;
}

.menu-item:hover {
  background: #f2f4f8;
}

.menu-item.active {
  background: #e4ecfb;
  color: #17509e;
}

.menu-item .ico {
  color: #5a6172;
  width: 22px;
  flex: 0 0 22px;
  text-align: center;
}

.menu-item:hover .ico,
.menu-item.active .ico {
  color: #17509e;
}

.menu-item-label {
  flex: 1;
}

.menu-item .chev {
  margin-left: auto;
  color: #c4cad6;
  font-size: 10px;
}

.broadcast {
  position: absolute;
  right: 4.5rem;
  top: 0.7em;
  text-transform: uppercase;
  font-weight: 600;
  background-color: #EE4242;
  color: white;
  padding: 0.25em 0.75em 0.25em 0.55em;
  border-radius: 5px;
  border: 2px solid white;

  i.fas {
    margin-right: 0.3em;
  }
}


.panel-options {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  bottom: auto;
  right: auto;
  width: 22em;
  min-height: 8em;
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
  overflow-x: hidden;
  background: $backgroundPanel;
  padding: 0.85em 1em;
  border: 1px solid $colorBorderPanelLink;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 17, 22, 0.14), 0 2px 6px rgba(15, 17, 22, 0.06);
  z-index: 100;

  h1 {
    font-size: 1.1rem;
    padding-top: 0.3rem !important;
    padding-bottom: 1rem !important;
  }

  table {
    background: none;
    width: 100%;
  }
}

/* wider flyout for the pipelines catalog (still comfortably on-canvas) */
.panel-options-wide {
  width: 26em;
}

/* ----- Metadata panel ---- */

.panel-metadata {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  right: auto;
  min-width: 30em;
  min-height: 30em;
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
  background: $backgroundPanel;
  padding: 0.85em 1em;
  border: 1px solid $colorBorderPanelLink;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 17, 22, 0.14), 0 2px 6px rgba(15, 17, 22, 0.06);
  z-index: 100;
}

/* ----- CUSTOM STYLE FOR OL CONTROLS ----- */

.ol-zoom, .ol-rotate {
  background: none !important;
  z-index: 20;
}

/* UI-5 (5c): zoom cluster anchored bottom-LEFT (design .zoom-cluster), opposite
   the navigator card which now floats bottom-right. */
.ol-zoom {
  top: auto !important;
  bottom: 0.5em !important;
  left: 0.5em !important;
}

.ol-rotate:not(.custom) {
  display: none;
}

.ol-control button {
  background: white !important;
  color: black !important;
  border-radius: 2px !important;
  box-shadow: 0 0 1px #777;

  &:hover {
    box-shadow: 0 0 1px black;
    cursor: pointer;
  }
}

.ol-zoom-in {
  margin-bottom: 5px !important;
}

/* ----- Rotation selector ----- */
.rotation-selector-wrapper {
  position: absolute;
  left: .5em;
  top: 5rem;
  z-index: 20;
}

/* ----- Vertical layers-opacity control (under the rotation compass) ----- */
.layers-opacity-fly {
  position: absolute;
  left: .5em;
  top: calc(5rem + 42px);
  width: 30px;
  height: 110px;
  background: #fff;
  border: 1px solid #e5e8ee;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(15, 17, 22, 0.12);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.layers-opacity-fly .lof-slider {
  width: 92px;                       /* becomes the vertical travel once rotated */
  transform: rotate(-90deg);         /* max at the TOP: slide up = more opaque */
  margin: 0;
  cursor: pointer;
  accent-color: #1e63d0;
}

/* UI-5 (5c): navigator/overview card floats bottom-RIGHT (design .overview-float).
   Full-float model: no docked panel bar, so it sits at the true right edge. */
.custom-overview {
  position: absolute;
  bottom: 0.5em;
  right: 0.5em;
  left: auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e8ee;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(15, 17, 22, 0.12);
  overflow: hidden;

  .ol-overviewmap {
    position: static;
    background: none;
  }

  .ol-overviewmap:not(.ol-collapsed) button {
    bottom: 2px !important;
  }

  .image-name {
    font-size: 0.8em;
    padding: 2px 5px;
    width: 158px;
    word-wrap: break-word;

    &.hidden {
      display: none;
    }
  }

  /* UI-5 (5c): magnification + coordinates readout under the navigator miniature */
  .nav-readout {
    padding: 3px 6px 5px;
    border-top: 1px solid #e5e8ee;
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-size: 0.68rem;
    color: #5a6172;
    font-variant-numeric: tabular-nums;

    &.hidden { display: none; }
    .nav-mag { font-weight: 600; color: #0e1116; }
    /* UI-7 B3: zoomed past scanned magnification → red (matches ScaleLine interpolation) */
    .nav-mag.nav-mag-over { color: #c8443c; }
    .nav-coords { color: #8a91a1; }
    .nav-scale {
      display: flex;
      align-items: flex-end;
      gap: 6px;
      margin-bottom: 2px;
    }
    .nav-scale-bar {
      display: inline-block;
      height: 6px;
      border: 2px solid #0e1116;
      border-top: 0;
      flex: 0 0 auto;
    }
    .nav-scale-label { color: #0e1116; font-weight: 600; }
  }

  /* UI-5 + UI-7 B9: slide-label toggle and full-screen toggle on one aligned row */
  .sl-controls {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 2px 5px 5px;

    &.hidden { display: none; }
  }

  .sl-nav-toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 1px solid #e5e8ee;
    border-radius: 5px;
    background: #fff;
    color: #1e63d0;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 7px;
    cursor: pointer;
    height: 24px;

    &.open { background: #e4ecfb; border-color: #1e63d0; }
  }

  /* icon-only full-screen button (label is shown as a hover tooltip), square so it
     lines up flush with the slide-label button on the same row */
  .sl-icon-only {
    gap: 0;
    width: 24px;
    justify-content: center;
    padding: 2px 0;
  }

  .sl-nav-card {
    margin: 0 5px 5px;
    width: 158px;
    border: 1px solid #e5e8ee;
    border-radius: 6px;
    background: #fff;
    padding: 4px;

    &.hidden { display: none; }

    .image-thumbnail {
      max-width: 148px;
      max-height: 150px;
      display: block;
    }
  }
}

/* ----- Image controls ----- */
.image-controls-wrapper {
  position: absolute;
  bottom: 1.5rem;
  left: 20%;
  right: 20%;
  z-index: 5;
}

/* ----- Annotation list ----- */
.annotations-table-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 40;
  pointer-events: none;
}

/* Dock layout: the wrapper stops being an overlay entirely - display:contents promotes the
   panel into .map-container's flex row (see AnnotationsList.vue). position/inset are reset
   because 'contents' + 'absolute' is a spec corner some engines blockify. */
.annotations-table-wrapper.docked {
  display: contents;
  position: static;
  inset: auto;
  z-index: auto;
  pointer-events: auto;
}

/* ----- UC segmentation overlay panel (spec 2026-06-19) — lives in the viewer dock ----- */
.seg-panel {
  padding: 0.6rem 0.8rem 0.8rem;
  font-size: 0.85rem;
}
.seg-toggle {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  cursor: pointer;
  font-weight: 600;
  margin: 0 0 0.55rem;
}
.seg-opacity {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}
.seg-opacity input[type='range'] {
  flex: 1;
  min-width: 0;
}
.seg-lbl {
  color: #555;
}
.seg-group {
  margin-bottom: 0.35rem;
}
.seg-group-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 600;
  padding: 0.1rem 0;
}
.seg-group-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.seg-classes {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
}
.seg-classes.nested {
  margin-left: 0.55rem;
  padding-left: 0.5rem;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.seg-classes li {
  margin: 0;
}
.seg-class {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  cursor: pointer;
  padding: 0.15rem 0;
  margin: 0;
}
.seg-swatch {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex: 0 0 auto;
}
.seg-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ----- Segmentation hover tooltip (follows the cursor) ----- */
.tile-error-notice {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 4px;
  background: rgba(181, 88, 12, 0.95);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.seg-hover-tip {
  position: fixed;
  z-index: 60;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(17, 20, 26, 0.92);
  color: #fff;
  font-size: 0.78rem;
  line-height: 1;
  padding: 0.32rem 0.5rem;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}
.seg-hover-swatch {
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  flex: 0 0 auto;
}
.seg-hover-name {
  font-weight: 500;
}
</style>
